var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { remove as arrayRemove } from 'lodash';
import { LogLevel, } from './../';
import { arraysMatch, enumIncludes, forEach, forOwn, } from './../utils';
import EventBus, { EventScope } from '@openticket/lib-eventbus';
import { ShopError, } from './../internals/errors';
// Cart
import CartValidator from './validation';
import { addMetadataFromProduct, addMetadataToCollection, MetadataAppliesTo, MetadataType, } from './metadata';
import { CartAction, CartItemChildType, CartProduct, CartTicket, setValidationLang, TermsActions, } from '.';
// Shop
import { ShopConfig, } from './../shop/config';
import Price, { VATUpdateAction } from './../shop/price';
import { PricingMethod, } from './../shop/types';
// Reservator
import Reservator, { ReservationType, } from './reservator';
// Coupons
import Coupon, { CouponAction, CouponType, } from './../shop/coupon';
// Order builder
import Orderbuilder from './orderbuilder';
// Integrations
import { SeatsioService, } from '../integrations/seatsio';
import { slugError } from '../internals/errors/errors';
import { CartItemType } from './types';
/**
 *  Used to create a new cart instance that holds all the data for the state of the order.
 *
 *  In this file, some object order is important. In those cases the below explanation is important!
 *  Note, according to the ES6 spec Object.getOwnPropertyNames is defined to call the following (abstract) operations in order
 *   - GetOwnPropertyKeys (does not change order)
 *   - [[OwnPropertyKeys]]
 *  Order is therefore defined as:
 *   1. For each own property key P of O that is an integer index, in ascending numeric index order
 *   2. For each own property key P of O that is a String but is not an integer index, in property creation order
 *   3. For each own property key P of O that is a Symbol, in property creation order
 *  Therefore, this order will be deemed 'good enough'.
 *  See http://www.ecma-international.org/ecma-262/6.0/#sec-ordinary-object-internal-methods-and-internal-slots-ownpropertykeys
 */
export default class Cart extends EventScope {
    /**
     *  Constructing a new cart creates an *(empty)* shopping cart instance,
     *  the cart is immediately ready for use.
     *
     *  ```ts
     *  const cart: Cart = new Cart(shopData, settings);
     *  ```
     *
     *  #### Possible errors
     *  One type of error could be thrown while initializing a shopping cart.
     *
     *  | Error                                    | Reason                                                                    |
     *  | :--------------------------------------- | :------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | No or invalid shop data [ShopData](../interfaces/shopdata.html) provided  |
     *  |                                          | or no settings provided                                                   |
     */
    constructor(shop, config, logger, eventBus) {
        super(eventBus || new EventBus());
        this._eventScope = 'cart';
        /**
         *  Collection of cart items.
         *  This holds all reserved tickets and products except orphaned global products.
         *  The items are grouped per event and per (ticket/product) abstract instance
         *
         *  @category Cart
         */
        this.items = {};
        /**
         *  Collection of cart products.
         *  This holds all reserved global product not belonging to an event.
         *
         *  @category Cart
         */
        this.orphanedGlobalProducts = {
            map: {},
            collection: [],
        };
        /**
         *  Holding the checkout details of the cart, being updated every time the items change.
         *
         *  @category Cart
         */
        this.checkout_details = {
            vats: {
                percentages: {},
                total: 0,
            },
            payment_method_fee: 0,
            subtotal_price: 0,
            total_price: 0,
            busy: false,
        };
        /**
         *  The locale in which the booker is creating his order.
         *
         *  This should be one of the supported languages in the shop.
         *  This value will also be used by the API to determine
         *  in which language to render the tickets, etc..
         */
        this.locale = 'en_GB';
        /**
         *  Validation methods for the cart data.
         *
         *  @Category Cart
         */
        this.metadata = {};
        /**
         *  Holding all the applied coupons, including the data of
         *  the coupon and the number of times it is applied.
         */
        this._coupons = {};
        /**
         *  The number of current pending actions. Used to block the
         *  place order call while there are pending actions.
         */
        this._pendingActions = 0;
        /**
         *  Extra arbitrary data to add to the place order call
         */
        this.techData = {};
        if (!logger) {
            throw slugError('Failed to create cart, missing logger', ['cart', 'constructor', 'missing', 'logger']);
        }
        this.log = logger;
        if (!shop) {
            throw this.log._create(LogLevel.Fatal, ['cart', 'constructor', 'error', 'missing', 'shop']);
        }
        if (!shop.data) {
            throw this.log._create(LogLevel.Fatal, ['cart', 'constructor', 'error', 'missing', 'data']);
        }
        if (!config) {
            throw this.log._create(LogLevel.Fatal, ['cart', 'constructor', 'error', 'missing', 'config']);
        }
        this._shop = shop;
        this._data = shop.data;
        this.booker = factoryBookerCartMetadata(shop.data.email_validation_rule);
        this._config = new ShopConfig(config);
        this._reservator = new Reservator(this._config);
        this._orderbuilder = new Orderbuilder(this._config);
        this._reservationTime = shop.data.reservation_time ? shop.data.reservation_time : this._config.defaultReservationTime;
        this.reservationExpiration = {
            date: null,
            secondsRemaining: null,
        };
        this.flatItems = {
            products: {},
            tickets: {},
        };
        // Add the default booker metadata to the metadata collection
        forEach(this._data.booker_metadata, (metadata) => {
            addMetadataToCollection(metadata, this.metadata);
        });
        this.validator = new CartValidator(this);
        // Select the first payment method as default.
        const firstPaymentMethod = this._data.payment_methods[0];
        if (!firstPaymentMethod) {
            throw this.log._create(LogLevel.Fatal, ['cart', 'constructor', 'error', 'missing', 'paymentmethods']);
        }
        this.paymentMethod = {
            item: firstPaymentMethod,
            currency: this._data.currency || 'EUR',
            is_stripe: !!firstPaymentMethod.stripe,
        };
        //  TODO Depending on the data this can become more than one terms checkbox,
        //  so it is already an object instead of a value.
        //  Exact implementation spec is still somewhat unfinished, so not doing anything with that (YET!!).
        this.terms = {
            default: false,
        };
        if (this._data.seats_public_key) {
            this.seatsioInstance = new SeatsioService({
                workspaceKey: this._data.seats_public_key,
            });
        }
        this._priceFormatter = this._resetPriceFormatter();
        this._registerOnUnload();
        this._initAt = new Date();
    }
    /**
     *  Determines if any items exist in the cart
     */
    hasItems() {
        return Object.keys(this.flatItems.tickets).length > 0
            || Object.keys(this.flatItems.products).length > 0;
    }
    /**
     * Get cart ticket info (total count, total price, cart ticket id's)
     */
    getTicketInfo(ticketGuid) {
        const ticket = this._data.tickets_map[ticketGuid];
        let cartTickets = [];
        if (this.items[ticket.event_id]) {
            cartTickets = Object.values(this.items[ticket.event_id].tickets.map[ticketGuid] || {});
        }
        return this._cartEventItemResponse(ticket, cartTickets, CartItemChildType.Tickets);
    }
    /**
     * Get product ticket info (total count, total price, cart product id's)
     */
    getProductInfo(productGuid) {
        const product = this._data.products_map[productGuid];
        let cartProducts = [];
        if (this.items[product.event_id]) {
            cartProducts = Object.values(this.items[product.event_id].products.map[productGuid] || {});
        }
        return this._cartEventItemResponse(product, cartProducts, CartItemChildType.Products);
    }
    /**
     * Get cart ticket product info (total count, total price, cart ticket product id's)
     */
    getTicketProductInfo(cartTicketId, ticketProductGuid) {
        const cartTicket = this.flatItems.tickets[cartTicketId];
        const ticketProduct = this._data.products_map[ticketProductGuid];
        let cartTicketProducts = [];
        if (this.flatItems.tickets[cartTicketId]) {
            cartTicketProducts = Object.values(this.flatItems.tickets[cartTicketId].products.map[ticketProductGuid] || {});
        }
        return this._cartEventItemResponse(ticketProduct, cartTicketProducts, CartItemChildType.Products, cartTicket);
    }
    /**
     *
     *  Used to sync the data of items that are currently in
     *  the cart with the shop data that got updated.
     *
     *  Updates the cart items' pricing objects according
     *  to their (possibly) updated item and recomputes
     *  their pricing and checkout details.
     *
     *  (i) The Object.assign should be used to remove any saved
     *  data about applied coupons, because the applied coupons
     *  get re-applied.
     *
     *  (i) Only the pricing is relevant to copy from the
     *  item, because other data (such as the name) can be
     *  used directly out of the item's item, where the pricing
     *  is subject to changes because of coupons.
     *
     *  @internal
     *
     */
    _shopDataUpdated() {
        this._action(CartAction.Start);
        // Also recompute their optional state as it might have been changed
        forOwn(this.flatItems.products, (cartProduct) => {
            Object.assign(cartProduct.pricing, cartProduct.item.pricing);
            cartProduct.setOptional();
        });
        // Also update their min_price to make sure pricing computations are correct
        forOwn(this.flatItems.tickets, (cartTicket) => {
            Object.assign(cartTicket.pricing, cartTicket.item.pricing);
            cartTicket.min_price = cartTicket.item.min_price;
        });
        // Reset and re-apply the applied coupons
        forOwn(this._coupons, (coupon) => {
            this._resetCoupon(coupon);
            this._couponDiscounts(CouponAction.Add, coupon);
        });
        // Recomputes the cart item pricing and recomputes the checkout details.
        this._doneUpdatingItems();
        this._action(CartAction.End);
    }
    /**
     *  Add a ticket to the cart. This will only succeed if a reservation can be retrieved.
     *
     *  ```ts
     *  cart.addTicket(ticket.guid);
     *  ```
     *
     *  @param ticketGuid The guid of the ticket to add
     *
     *  | Error                                    | Reason                                                                    |
     *  | :--------------------------------------- | :------------------------------------------------------------------------ |
     *  | [ShopError](shoperror.html)              | No or invalid ticket in shop data [ShopData](../interfaces/shopdata.html) |
     *  | [ApiError](apierror.html)                | The shop API threw an error                                               |
     */
    addTicket(ticketGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const addTicketEvent = yield this._addTicket(ticketGuid);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['ticket', ticketGuid, 'add'], addTicketEvent);
                return addTicketEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addTicket', 'error', 'failed'], { ticketGuid });
            }
        });
    }
    /**
     *
     *  Internally used for adding a ticket to the cart.
     *
     *  @param ticketGuid The guid of the ticket to add.
     *  @param seatInfo When a ticket is added through the seats integration, it's place's info can be added here.
     *
     */
    _addTicket(ticketGuid, seatInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            const ticket = this._getShopTicket(ticketGuid);
            this._ensureTicketAvailable(ticket);
            const eventCollection = this._getOrCreateEventCollection(ticket.event_id);
            // Ensure the eventCollection ticket map is set on the cart and retrieve it
            const cartTicketMap = eventCollection.tickets.map[ticketGuid] = eventCollection.tickets.map[ticketGuid] || {};
            // Cart items in the cart are always in stable state.
            // E.g.: Pending reservations are not yet added to the cart item collections
            // until the reservation request resolves.
            // Here the count is calculated to come as close to a realised ticket count,
            // assuming all pending reservations will successfully resolve.
            const currentTicketCount = Object.keys(cartTicketMap).length + this._reservator.inFlightCount(ReservationType.Ticket, ticket.guid);
            // Calculate the amount of tickets to order to the next valid step
            const numberOfTicketsToReserve = seatInfo ? 1 : calculateTicketDiff(currentTicketCount, ticket.min_orderable_amount_per_order || 1, ticket.increment || 1);
            // Don't let it exceed the max orderable amount per order
            if ((currentTicketCount + numberOfTicketsToReserve) > ticket.max_orderable_amount_per_order) {
                throw new ShopError('Ticket max orderable amount per order reached');
            }
            try {
                const reservations = yield this._reservator.reserve(ReservationType.Ticket, ticket.guid, numberOfTicketsToReserve);
                const cartTickets = [];
                // Process every reservation, create items for each and add to the cart collection(s)
                forEach(reservations, (reservation) => {
                    // Create the cart ticket
                    const cartTicket = new CartTicket(this._data, ticket, reservation, undefined, seatInfo);
                    // Add the cart ticket to the tickets map and collection of his event
                    cartTicketMap[cartTicket.id] = cartTicket;
                    eventCollection.tickets.collection.push(cartTicket);
                    // Add it to the flat items collection for easy access
                    this.flatItems.tickets[cartTicket.id] = cartTicket;
                    // Add the cart ticket's children (products) to the flat list
                    // (!) Not to the event's products, as those are only for top level products
                    forOwn(cartTicket.products.map, (cartProducts) => {
                        forOwn(cartProducts, (cartProduct) => {
                            this.flatItems.products[cartProduct.id] = cartProduct;
                        });
                    });
                    cartTickets.push(cartTicket);
                });
                this._itemsUpdated();
                return this._cartEventItemResponse(ticket, cartTickets, CartItemChildType.Tickets);
            }
            catch (e) {
                if (e && e.slug === 'sdkshop.log.reservator.reserve.error.notacceptable' && e.response && e.response.data) {
                    if (e.response.data.error_description === 'Sold out') {
                        ticket.status = 'sold_out';
                        throw this.log._passThroughOrCreate(e, LogLevel.Warning, ['cart', 'addTicket', 'error', 'sold_out'], { ticketGuid });
                    }
                    if (e.response.data.error_description === 'Everything is reserved') {
                        throw this.log._passThroughOrCreate(e, LogLevel.Warning, ['cart', 'addTicket', 'error', 'all_reserved'], { ticketGuid });
                    }
                }
                // TODO: add check for not_sold_right_now
                throw e;
            }
        });
    }
    /**
     *  Remove a ticket from the cart.
     *
     *  ```ts
     *  cart.removeTicket(ticket.guid);
     *  cart.removeTicket(ticket.guid, cartTicket.id);
     *  ```
     *
     *  @param ticketGuid The guid of the ticket to remove
     *  @param cartTicketId The id of the cart ticket, when not provided the last cart item will be removed
     *
     *  | Error                                    | Reason                                    |
     *  | :--------------------------------------- | :---------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid ticket in shop data or cart |
     *  |                                          | [ShopData](../interfaces/shopdata.html)   |
     *  | [ApiError](apierror.html)                | The shop API threw an error               |
     */
    removeTicket(ticketGuid, cartTicketId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const removeTicketEvent = yield this._removeTicket(ticketGuid, cartTicketId);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['ticket', ticketGuid, 'remove'], removeTicketEvent);
                return removeTicketEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeTicket', 'error', 'failed'], { ticketGuid, cartTicketId });
            }
        });
    }
    /**
     *
     *  Internally used to remove a ticket from the cart, including the option to not
     *  recompute the checkout details after doing so. Used by the removeAllItems
     *  method to make sure the re-computation of the checkout details only happens
     *  once after every item has been removed.
     *
     *  @param ticketGuid The guid of the ticket to remove
     *  @param cartTicketId The id of the cart ticket, when not provided the last cart item  will be removed
     *  @param singleRemove If it's about the removing of a single product (and not removing all products)
     *
     */
    _removeTicket(ticketGuid, cartTicketId, singleRemove = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const ticket = this._getShopTicket(ticketGuid);
            // Retrieve the cart ticket map and cart ticket collection
            const [cartTicketCollection, cartTicketMap] = this._getTicketItemCollection(ticket.event_id, ticket.guid);
            // Any pending counts are not relevant (yet) for removing tickets
            const cartTicketCount = Object.keys(cartTicketMap).length;
            const cartTicketIds = [];
            if (cartTicketId) {
                // When a specific cart ticket id is provided, only that cart ticket should be removed
                if (!Object.prototype.hasOwnProperty.call(cartTicketMap, cartTicketId)) {
                    throw this.log._create(LogLevel.Error, ['cart', 'removeTicket', 'error', 'notfound'], {
                        ticketGuid,
                        cartTicketId,
                    });
                }
                cartTicketIds.push(cartTicketId);
            }
            else if (cartTicketCount) {
                // When no specific cart ticket id is provided, remove the n last added ticket(s)
                // (i) See top of file comment; sorting of getOwnPropertyNames is good enough
                const numberOfTickets = calculateTicketDiff(cartTicketCount, ticket.min_orderable_amount_per_order || 1, ticket.increment || 1, -1);
                cartTicketIds.push(...Object.getOwnPropertyNames(cartTicketMap).slice((-1) * numberOfTickets));
            }
            else {
                throw new ShopError(`Ticket is not in cart's item collection: ${ticket.guid}`);
            }
            // Array containing all cart tickets to be released
            const cartTickets = [];
            // Process all cart ticket IDs to be removed.
            forEach(cartTicketIds, (currentCartTicketId) => {
                // Get the ticket out of the collection to release it in the API and remove it from the event's collection
                const cartTicket = cartTicketMap[currentCartTicketId];
                cartTickets.push(cartTicket);
                // (!) The reservation release request is not awaited!
                //
                // Race conditions will become a lot more common
                // and state is almost guaranteed to fail when waiting for reservation release!
                //
                // Note: It is possible a reservation will not be properly released in the API,
                // but is removed from the cart.
                // This is accepted behaviour as at some point the API will release the reservation anyway,
                // when the reservation timeout has been reached.
                this._reservator.release(ReservationType.Ticket, cartTicket.item.guid, cartTicket.reservation.reservation)
                    .catch(() => { });
                // Remove the cart ticket from the map
                delete cartTicketMap[cartTicket.id];
                // Remove collection element from array by value
                arrayRemove(cartTicketCollection, cartTicket);
                // Remove the cart ticket from the flat items
                delete this.flatItems[CartItemChildType.Tickets][cartTicket.id];
                // Make ticket available if sold_out on release
                if (cartTicket.item.status === 'sold_out') {
                    cartTicket.item.status = 'available';
                }
                // Remove the child items out of the flat items and check if coupons were applied to keep the coupon stats up to date
                // (i) Product reservations (should) have been released automatically by the API
                forOwn(cartTicket.products.map, (cartProducts) => {
                    forOwn(cartProducts, (cartProduct) => {
                        delete this.flatItems.products[cartProduct.id];
                        this._checkRemovedProductCoupons(cartProduct);
                    });
                });
            });
            // Any empty elements in the cart structure can/should be removed.
            // In this case it is not necessary to check the entire structure, only a partial subset.
            this._cleanUpItemStructure(ticket.event_id, ticket.guid);
            // Only invoke the items update when it's about a single remove,
            // otherwise the overlaying action that is removing multiple
            // is responsible for invoking the items updated
            if (singleRemove) {
                this._itemsUpdated();
            }
            return this._cartEventItemResponse(ticket, cartTickets, CartItemChildType.Tickets);
        });
    }
    /**
     *  Add a (global) product to the cart. This will only succeed if a reservation can be retrieved.
     *
     *  ```ts
     *  cart.addProduct(product.guid);
     *  ```
     *
     *  @param productGuid The guid of the (global) product to add
     *
     *  | Error                                    | Reason                                                                     |
     *  | :--------------------------------------- | :------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid product in shop data [ShopData](../interfaces/shopdata.html) |
     *  | [ApiError](apierror.html)                | The shop API threw an error                                                |
     */
    addProduct(productGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const addProductEvent = yield this._addProduct(productGuid);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['product', productGuid, 'add'], addProductEvent);
                return addProductEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addProduct', 'error', 'failed'], { productGuid });
            }
        });
    }
    /**
     *
     *  Internally used to add a (global) product to the cart.
     *
     *  @param productGuid The guid of the (global) product to add
     *
     */
    _addProduct(productGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const product = this._getShopProduct(productGuid);
            // Global products can belong to an event OR be a child of the shop/cart directly
            const event = product.event_id ? this._data.events_map[product.event_id] : undefined;
            const globalProductsCollection = event ? event.global_products : this._data.global_products;
            // Above the product is retrieved from the root collection.
            // This contains both ticket products and global products.
            // Ensure the product is a global product.
            this._ensureGlobalProduct(globalProductsCollection, product);
            // Retrieve the relevant global product collection depending on the event.
            let productCollection;
            if (event) {
                productCollection = this._getOrCreateEventCollection(product.event_id)[CartItemChildType.Products];
            }
            else {
                productCollection = this.orphanedGlobalProducts;
            }
            const cartProductMap = productCollection.map[product.guid] = productCollection.map[product.guid] || {};
            const reservations = yield this._reservator.reserve(ReservationType.Product, product.guid);
            const cartProducts = [];
            // Process every reservation, create items for each and add to the cart collection(s)
            forEach(reservations, (reservation) => {
                // Create the cart product
                const cartProduct = new CartProduct(this._data, product, reservation);
                // Add the cart product to the event's product map and collection
                cartProductMap[cartProduct.id] = cartProduct;
                productCollection.collection.push(cartProduct);
                // Add it to the flat items collection for easy access
                this.flatItems.products[cartProduct.id] = cartProduct;
                cartProducts.push(cartProduct);
            });
            // Add the product's person meta to the cart's metadata
            addMetadataFromProduct(product, this.metadata, MetadataAppliesTo.Person);
            this._itemsUpdated();
            return this._cartEventItemResponse(product, cartProducts, CartItemChildType.Products);
        });
    }
    /**
     *  Remove a (global) product from the cart.
     *
     *  ```ts
     *  cart.removeProduct(product.guid);
     *  cart.removeProduct(product.guid, cartProduct.id);
     *  ```
     *
     *  @param productGuid The guid of the (global) product to remove
     *  @param cartProductId The id of the cart product, when not provided the last cart product will be removed
     *
     *  | Error                                    | Reason                                    |
     *  | :--------------------------------------- | :---------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid ticket in shop data or cart |
     *  |                                          | [ShopData](../interfaces/shopdata.html)   |
     *  | [ApiError](apierror.html)                | The shop API threw an error               |
     */
    removeProduct(productGuid, cartProductId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const removeProductEvent = yield this._removeProduct(productGuid, cartProductId);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['product', productGuid, 'remove'], removeProductEvent);
                return removeProductEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeProduct', 'error', 'failed'], { productGuid, cartProductId });
            }
        });
    }
    /**
     *
     *  Internally used to remove a (global) product from the cart, including the option to
     *  not recompute the checkout details after doing so. Used by the removeAllItems
     *  method to make sure the re-computation of the checkout details only happens
     *  once after every item has been removed.
     *
     *  @param productGuid The guid of the (global) product to remove
     *  @param cartProductId The id of the cart product, when not provided the last (global) cart product will be removed
     *  @param singleRemove If it's about the removing of a single product (and not removing all products)
     *
     */
    _removeProduct(productGuid, cartProductId, singleRemove = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const product = this._getShopProduct(productGuid);
            // Global products can belong to an event OR be a child of the shop/cart directly
            const event = product.event_id ? this._data.events_map[product.event_id] : undefined;
            const globalProductsCollection = event ? event.global_products : this._data.global_products;
            // Above the product is retrieved from the root collection.
            // This contains both ticket products and global products.
            // Ensure the product is a global product.
            this._ensureGlobalProduct(globalProductsCollection, product);
            // Retrieve the relevant global product collection depending on the event.
            const [cartProductCollection, cartProductMap] = this._getProductItemCollection(event ? event.guid : undefined, product.guid);
            const cartProductCount = Object.keys(cartProductMap).length;
            const cartProductIds = [];
            if (cartProductId) {
                if (!Object.prototype.hasOwnProperty.call(cartProductMap, cartProductId)) {
                    throw new ShopError(`Cart product id not found: ${cartProductId}`);
                }
                cartProductIds.push(cartProductId);
            }
            else if (cartProductCount) {
                // Remove the n last added product(s)
                // (i) See top of file comment; sorting of getOwnPropertyNames is good enough
                cartProductIds.push(...Object.getOwnPropertyNames(cartProductMap).slice(-1));
            }
            else {
                throw new ShopError(`Product is not in cart's item collection: ${product.guid}`);
            }
            // Array containing all cart tickets to be released
            const cartProducts = [];
            forEach(cartProductIds, (currentCartProductId) => {
                // Get the product out of the collection to release it in the API and remove it from the event's collection
                const cartProduct = cartProductMap[currentCartProductId];
                cartProducts.push(cartProduct);
                // (!) The reservation release request is not awaited!
                //
                // Race conditions will become a lot more common
                // and state is almost guaranteed to fail when waiting for reservation release!
                //
                // Note: It is possible a reservation will not be properly released in the API,
                // but is removed from the cart.
                // This is accepted behaviour as at some point the API will release the reservation anyway,
                // when the reservation timeout has been reached.
                this._reservator.release(ReservationType.Product, cartProduct.item.guid, cartProduct.reservation.reservation)
                    .catch(() => { });
                // Remove the cart ticket from the map
                delete cartProductMap[cartProduct.id];
                arrayRemove(cartProductCollection, cartProduct);
                // Remove the cart product from the flat items
                delete this.flatItems[CartItemChildType.Products][cartProduct.id];
                // Check if the coupons should be updated
                this._checkRemovedProductCoupons(cartProduct);
            });
            this._cleanUpItemStructure(product.event_id, undefined, product.guid);
            // Remove the metadata to ask out of the cart's metadata
            this._checkRemovedProductMetadata(product.metadata);
            // Only invoke the items update when it's about a single remove,
            // otherwise the overlaying action that is removing multiple
            // is responsible for invoking the items updated
            if (singleRemove) {
                this._itemsUpdated();
            }
            return this._cartEventItemResponse(product, cartProducts, CartItemChildType.Products);
        });
    }
    /**
     *  Add a product to a cart ticket. This will only succeed if a reservation can be retrieved.
     *
     *  ```ts
     *  cart.addProductToTicket(cartTicket.id, product.guid);
     *  ```
     *
     *  @param cartTicketId The id of the cart ticket to add the product to
     *  @param productGuid The guid of the product to add to the ticket
     *
     *  | Error                                    | Reason                                                                     |
     *  | :--------------------------------------- | :------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid product in shop data [ShopData](../interfaces/shopdata.html) |
     *  | [ApiError](apierror.html)                | The shop API threw an error                                                |
     */
    addProductToTicket(cartTicketId, productGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const addProductToTicketEvent = yield this._addProductToTicket(cartTicketId, productGuid);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['ticket', cartTicketId, 'product', productGuid, 'add'], addProductToTicketEvent);
                return addProductToTicketEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addProductToTicket', 'error', 'failed'], { cartTicketId, productGuid });
            }
        });
    }
    /**
     *
     *  Internally used to add a product to a cart ticket.
     *
     *  @param cartTicketId The id of the cart ticket to add the product to
     *  @param productGuid The guid of the product to add to the ticket
     *
     */
    _addProductToTicket(cartTicketId, productGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            // Get the ticket to add the product to
            const cartTicket = this._getCartTicket(cartTicketId);
            // Number of times the product has been added to the ticket (already)
            const timesAdded = cartTicket.products.map[productGuid]
                ? Object.keys(cartTicket.products.map[productGuid]).length
                : 0;
            forEach(cartTicket.item.groups, (productGroup) => {
                let inGroup = false;
                let totalAdded = 0;
                forEach(productGroup.products, (groupProduct) => {
                    if (productGuid === groupProduct.guid) {
                        inGroup = true;
                        const uniqueness = groupProduct.uniqueness || productGroup.uniqueness;
                        // Check if the uniqueness restriction will be exceeded
                        // This is either the group's product pivot value, or the product group value
                        if (timesAdded >= uniqueness) {
                            throw new ShopError(`Adding product exceeds uniqueness (${uniqueness}) of product group: ${productGroup.guid}`);
                        }
                    }
                    // Count the group's products that are added to the
                    // ticket already, for checking the max bound
                    if (cartTicket.products.map[groupProduct.guid]) {
                        totalAdded += Object.keys(cartTicket.products.map[groupProduct.guid]).length;
                    }
                });
                // When the product is in this group, check if the max_bound will be exceeded
                if (inGroup && totalAdded >= productGroup.max_bound) {
                    throw new ShopError(`Adding product exceeds max_bound (${productGroup.max_bound}) of product group: ${productGroup.guid}`);
                }
            });
            // Get the product to add
            const ticketProduct = this._getProductFromTicket(cartTicket.item, productGuid);
            // Reserve the product as child of the (cart) ticket
            const reservations = yield this._reservator.reserveChild(ReservationType.Ticket, cartTicket.item.guid, ReservationType.Product, ticketProduct.item.guid);
            // Let the ticket process his (child) product reservations
            const cartProducts = cartTicket._processProductChildReservation(ticketProduct.item.guid, ...reservations);
            // Add the cart products to the flat items for easy access
            forEach(cartProducts, (cartProduct) => {
                this.flatItems.products[cartProduct.id] = cartProduct;
            });
            // Only add the metadata if the ticket's personalization should
            // not be later, because the buyer doesn't need to enter the
            // metadata now if it should be personalized later
            if (!cartTicket.item.late_personalization) {
                addMetadataFromProduct(ticketProduct.item, cartTicket.metadata, MetadataAppliesTo.Person);
            }
            this._itemsUpdated();
            return this._cartEventItemResponse(ticketProduct.item, cartProducts, CartItemChildType.Products, cartTicket);
        });
    }
    /**
     *  Remove a product from a cart ticket. This will only succeed if a reservation can be cancelled.
     *
     *  ```ts
     *  cart.removeProductFromTicket(cartTicket.id, product.guid);
     *  cart.removeProductFromTicket(cartTicket.id, product.guid, cartProduct.id);
     *  ```
     *
     *  @param cartTicketId The id of the ticket to remove the product from
     *  @param productGuid The guid of the product to remove
     *  @param cartProductId The id of the cart ticket, when not provided the last cart ticket will be removed
     *
     *  | Error                                    | Reason                                                             |
     *  | :--------------------------------------- | :----------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid cart product [ShopData](../interfaces/shopdata.html) |
     *  | [ApiError](apierror.html)                | The shop API threw an error                                        |
     */
    removeProductFromTicket(cartTicketId, productGuid, cartProductId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Also ends the action via the _itemsUpdated()
                const removeTicketProductEvent = yield this._removeProductFromTicket(cartTicketId, productGuid, cartProductId);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                this.emit(['ticket', cartTicketId, 'product', productGuid, 'remove'], removeTicketProductEvent);
                return removeTicketProductEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeProductFromTicket', 'error', 'failed'], { cartTicketId, productGuid, cartProductId });
            }
        });
    }
    /**
     *
     *  Internally used to remove a product from a cart ticket.
     *
     *  @param cartTicketId The id of the ticket to remove the product from
     *  @param productGuid The guid of the product to remove
     *  @param cartProductId The id of the cart ticket, when not provided the last cart ticket will be removed
     */
    _removeProductFromTicket(cartTicketId, productGuid, cartProductId) {
        return __awaiter(this, void 0, void 0, function* () {
            const cartTicket = this._getCartTicket(cartTicketId);
            const ticketProduct = this._getProductFromTicket(cartTicket.item, productGuid);
            // Retrieve the relevant product collection.
            const [cartProductCollection, cartProductMap] = this._getChildProductCollection(cartTicket, productGuid);
            const cartProductCount = Object.keys(cartProductMap).length;
            const cartProductIds = [];
            if (cartProductId) {
                if (!Object.prototype.hasOwnProperty.call(cartProductMap, cartProductId)) {
                    throw new ShopError(`Cart product id not found: ${cartProductId}`);
                }
                cartProductIds.push(cartProductId);
            }
            else if (cartProductCount) {
                // Remove the n last added products(s)
                // (i) See top of file comment; sorting of getOwnPropertyNames is good enough
                cartProductIds.push(...Object.getOwnPropertyNames(cartProductMap).slice(-1));
            }
            else {
                throw new ShopError(`Product is not in cart ticket's product collection: ${ticketProduct.item.guid}`);
            }
            // Array containing all cart products to be released
            const cartProducts = [];
            forEach(cartProductIds, (currentCartProductId) => {
                // Get the product out of the collection to release it in the API and remove it from the cart ticket's collection
                const cartProduct = cartProductMap[currentCartProductId];
                cartProducts.push(cartProduct);
                // (!) The reservation release request is not awaited!
                //
                // Race conditions will become a lot more common
                // and state is almost guaranteed to fail when waiting for reservation release!
                //
                // Note: It is possible a reservation will not be properly released in the API,
                // but is removed from the cart.
                // This is accepted behaviour as at some point the API will release the reservation anyway,
                // when the reservation timeout has been reached.
                this._reservator.release(ReservationType.Product, cartProduct.item.guid, cartProduct.reservation.reservation)
                    .catch(() => { });
                // Remove the cart product from the map
                delete cartProductMap[cartProduct.id];
                arrayRemove(cartProductCollection, cartProduct);
                // Remove the cart product from the flat items
                delete this.flatItems[CartItemChildType.Products][cartProduct.id];
                // Check if coupons were applied to keep the coupon stats up to date
                this._checkRemovedProductCoupons(cartProduct);
            });
            // Remove any (optional) parent structure if empty
            if (!Object.keys(cartTicket.products.map[ticketProduct.item.guid]).length) {
                delete cartTicket.products.map[ticketProduct.item.guid];
            }
            // Only remove the metadata if the product's ticket's personalization
            // should not be later, because the buyer doesn't need to enter the
            // metadata now if it should be personalized later
            if (!cartTicket.item.late_personalization) {
                cartTicket._checkRemovedProductMetadata(ticketProduct.item.metadata);
            }
            this._itemsUpdated();
            return this._cartEventItemResponse(ticketProduct.item, cartProducts, CartItemChildType.Products, cartTicket);
        });
    }
    /**
     *  Add (a) ticket(s) through the seats selector
     *
     *  Note: The seat selector will be opened for all tickets
     *  with the same set of seats.io eventKeys as the provided ticket.
     *
     *  @param ticketGuid The ticket guid for which to open the seat selector.
     *  @param divId The id of the DOM element to mount seat selector in.
     *  @param inputOptions Extra options to forward to the seat selector.
     */
    addTicketThroughSeats(ticketGuid, divId, inputOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                this.emit(['seats', 'created'], null, true);
                yield this._openSeatsFromTicket(ticketGuid, divId, inputOptions);
                this.emit(['seats', 'mounted'], null, true);
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addTicketThroughSeats', 'error', 'failed'], { ticketGuid });
            }
        });
    }
    /**
     *  Find a cart ticket by it's seats info object label and remove it from the cart.
     *
     *  @param label The seats.io seating chart object's label
     *  @param ticketGuid The ticket guid from the seats.io pricing ticket type
     */
    _removeTicketBySeats(label, ticketGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const cartTicketMap = ticketGuid
                ? this._getTicketItemCollection(this._getShopTicket(ticketGuid).event_id, ticketGuid)[1]
                : this.flatItems[CartItemChildType.Tickets];
            const labeledCartTickets = Object.getOwnPropertyNames(cartTicketMap)
                .map((cartTicketId) => cartTicketMap[cartTicketId])
                .filter((cartTicket) => cartTicket.seatsInfo && cartTicket.seatsInfo.objectLabel === label)
                .slice(-1);
            if (!labeledCartTickets.length) {
                console.warn('Failed to remove seated ticket -> Could not find ticket: ', label, ticketGuid);
                return;
            }
            return yield this._removeTicket(labeledCartTickets[0].item.guid, labeledCartTickets[0].id);
        });
    }
    /**
     *  Collect all (nested) event keys from the ticket
     *
     *  Note: If any optional products are connected to an event date with a seats_event_key,
     *  those are ignored (for now...)
     *  Might want to add those to addProductToTicketThroughSeats once the API supports it.
     */
    _getEventKeysFromTicket(ticket, productType) {
        const eventKeys = {};
        forEach(ticket.products[productType], (product) => {
            forEach(product.item.event_dates, (eventDate) => {
                // By using a map, this ensures the event date collection is unique
                if (eventDate.seats_event_key) {
                    eventKeys[eventDate.seats_event_key] = eventDate.seats_event_key;
                }
            });
        });
        return Object.keys(eventKeys);
    }
    /**
     *  Open the seats selector with the intention of adding tickets to the cart
     *
     *  Note: The seat selector will be opened for all tickets
     *  with the same set of seats.io eventKeys as the provided ticket.
     *
     *  @param ticketGuid The ticket guid for which to open the seat selector.
     *  @param divId The id of the DOM element to mount seat selector in.
     *  @param inputOptions Extra options to forward to the seat selector.
     */
    _openSeatsFromTicket(ticketGuid, divId, inputOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.seatsioInstance) {
                throw new ShopError('Ticket is not seated -> Seats.io instance not constructed');
            }
            const [tickets, eventKeys] = this._getSeatedTickets(ticketGuid);
            const pricing = {};
            // Filter any tickets that have the exact same combination of event keys
            // If any other combination can also be selected, a mismatch between the ticket spec,
            // the seats reservations and the ordered products can occur!!!
            forEach(tickets, (otherTicket) => {
                pricing[otherTicket.seats_category_key] = pricing[otherTicket.seats_category_key] || {
                    category: otherTicket.seats_category_key,
                    ticketTypes: [],
                };
                pricing[otherTicket.seats_category_key].ticketTypes.push({
                    ticketType: otherTicket.guid,
                    price: otherTicket.pricing.total_price,
                    label: otherTicket.name,
                });
            });
            const seatsOptions = Object.assign(Object.assign({ 
                // Default values from data / cart, can be overwritten by input options!
                language: this.locale, allowOrphanSeats: this._data.seats_allow_orphan_computed || false, forceConsecutiveSeats: false, colorScheme: 'light', priceFormatter: (cents) => this._formatPrice(cents) }, inputOptions), { 
                // Always set by the computed values, can never be set through input options
                divId: divId, events: eventKeys, 
                // This line shows a confirmation dialog even when only one choice.
                pricing: Object.values(pricing), 
                // The below code block could be used to convert single choice categories to the simple pricing config...
                // Note however: the callbacks will need to be rewritten.
                // Without the ticket type info, they can not easily retrieve the associated ticket!!!
                //
                // pricing: Object.values(pricing).map((pricingConfig: seatsio.PricingConfig) => {
                //
                //     if (pricingConfig.ticketTypes && pricingConfig.ticketTypes.length === 1) {
                //
                //         return {
                //             category: pricingConfig.category,
                //             price: pricingConfig.ticketTypes[0].price,
                //         };
                //
                //     }
                //
                //     return pricingConfig;
                //
                // }),
                onHoldSucceeded: (objects, ticketTypes) => __awaiter(this, void 0, void 0, function* () {
                    if (!ticketTypes) {
                        // TODO Retrieve ticket by seats_category_key ???
                        console.error('Invalid hold -> no ticket types', objects);
                        throw new Error('Invalid hold -> no ticket types');
                    }
                    this._action(CartAction.Start);
                    forEach(objects, (object, index) => {
                        const ticketType = ticketTypes[index];
                        if (!ticketType.ticketType) {
                            // TODO Retrieve ticket by seats_category_key ???
                            console.error('Invalid hold -> no ticket type', object);
                            return;
                        }
                        const seatInfo = {
                            objectLabel: object.label,
                            eventKeys,
                        };
                        // Note: If the ticket is not available, this will be handled by `_addTicket`
                        this._addTicket(ticketType.ticketType, seatInfo)
                            .then((addTicketEvent) => {
                            this.emit(['ticket', ticketGuid, 'add'], addTicketEvent);
                            return addTicketEvent;
                        })
                            .catch((reason) => {
                            object.deselect();
                            console.error(`Failed to add ticket for seated object ${object.label}`, reason);
                        });
                    });
                    // Ends the action and emits the checkout details
                    this._action(CartAction.End);
                }), onHoldFailed: (objects, ticketTypes) => {
                    console.error('onHoldFailed', objects, ticketTypes);
                    // TODO Some error for frontend ???
                }, onHoldTokenExpired: () => {
                    console.error('onHoldTokenExpired');
                    this.emit(['seats', 'error', 'hold-expired'], null, true);
                    // TODO -> release all seated tickets or reselect seated tickets (if possible), or both...
                }, onReleaseHoldSucceeded: (objects, ticketTypes) => __awaiter(this, void 0, void 0, function* () {
                    this._action(CartAction.Start);
                    forEach(objects, (object, index) => {
                        const ticketType = ticketTypes ? ticketTypes[index] : undefined;
                        this._removeTicketBySeats(object.label, ticketType ? ticketType.ticketType : undefined)
                            .then((removeTicketEvent) => {
                            if (removeTicketEvent) {
                                this.emit(['ticket', ticketGuid, 'remove'], removeTicketEvent);
                            }
                            return removeTicketEvent;
                        })
                            .catch((reason) => {
                            console.error(`Failed to remove ticket for seated object ${object.label}`, reason);
                        });
                    });
                    // Ends the action and emits the checkout details
                    this._action(CartAction.End);
                }), onReleaseHoldFailed: (objects, ticketTypes) => __awaiter(this, void 0, void 0, function* () {
                    console.error('onReleaseHoldFailed', objects, ticketTypes);
                    // TODO Did only the release hold fail, or also the deselect?
                    //  Or.. is the object deselected -> we should also release it, regardless of hold failure!
                    //  If the object is NOT deselected current behaviour is fine!
                    // TODO Some error for frontend ???
                }), onSelectionValid: () => {
                    this.emit(['seats', 'validation', 'valid'], {
                        valid: true,
                        violations: [],
                    }, true);
                }, onSelectionInvalid: (violations) => {
                    console.error('onSelectionInvalid', violations);
                    this.emit(['seats', 'validation', 'invalid'], {
                        valid: false,
                        violations: violations,
                    }, true);
                } });
            yield this.seatsioInstance.mount(seatsOptions);
        });
    }
    /**
     *  Close the seats io seating chart
     */
    closeSeats() {
        if (!this.seatsioInstance) {
            throw this.log._create(LogLevel.Warning, ['cart', 'closeSeats', 'error', 'initialized']);
        }
        this.seatsioInstance.destroy();
        this._action(CartAction.End);
        this.emit(['seats', 'destroy'], null, true);
    }
    /**
     *  Retrieve the seats info for the set of (cart) tickets equal to the given ticket guid.
     *
     *  @param ticketGuid The reference ticket's guid
     */
    getSeatedTicketInfo(ticketGuid) {
        const [tickets, eventKeys] = this._getSeatedTickets(ticketGuid);
        const allCartItems = [];
        forEach(tickets, (ticket) => {
            try {
                allCartItems.push(...Object.values(this._getTicketItemCollection(ticket.event_id, ticket.guid)[1]));
            }
            catch (e) {
                // Ignore...
            }
        });
        return {
            items: allCartItems.map((cartTicket) => {
                return {
                    ticket_guid: cartTicket.item.guid,
                    name: cartTicket.item.name,
                    id: cartTicket.id,
                    label: cartTicket.seatsInfo ? cartTicket.seatsInfo.objectLabel : '',
                    pricing: cartTicket.pricing,
                };
            }),
            eventKeys: eventKeys,
            count: allCartItems.length,
            pricing: Price.getCumulativeCartItemPrice(allCartItems),
        };
    }
    /**
     *  Retrieve all seated tickets with the same set of event keys to the given ticket guid.
     *
     *  @param ticketGuid The reference ticket's guid
     */
    _getSeatedTickets(ticketGuid) {
        const ticket = this._getShopTicket(ticketGuid);
        if (!ticket.seats_category_key) {
            throw new ShopError('Ticket is not seated -> Seats.io category key not found');
        }
        if (!ticket.seated) {
            throw new ShopError('Ticket is not seated');
        }
        const eventKeys = this._getEventKeysFromTicket(ticket, 'required');
        if (!eventKeys.length) {
            throw new ShopError('Ticket is not seated -> Seats.io event key(s) not found');
        }
        // TODO Note: currently no way to know if the event Keys belong to the same chart...
        const event = this._getShopEvent(ticket.event_id);
        const tickets = [];
        // Filter any tickets that have the exact same combination of event keys
        // If any other combination can also be selected, a mismatch between the ticket spec,
        // the seats reservations and the ordered products can occur!!!
        forEach(event.tickets, (otherTicket) => {
            if (otherTicket.seats_category_key) {
                if (!this._ensureTicketAvailable(otherTicket, false)) {
                    return;
                }
                const ticketEventKeys = this._getEventKeysFromTicket(otherTicket, 'required');
                if (ticketEventKeys.length && arraysMatch(eventKeys, ticketEventKeys)) {
                    tickets.push(otherTicket);
                }
            }
        });
        return [tickets, eventKeys];
    }
    /**
     *  Retrieve a cart ticket from the cart
     *
     *  If it does not exist a shop error is thrown
     */
    _getCartTicket(cartTicketId) {
        if (!cartTicketId || !Object.prototype.hasOwnProperty.call(this.flatItems[CartItemChildType.Tickets], cartTicketId)) {
            throw new ShopError(`No valid cart ticket id provided: ${cartTicketId}`);
        }
        return this.flatItems[CartItemChildType.Tickets][cartTicketId];
    }
    // noinspection JSMethodCanBeStatic
    /**
     *  Retrieve a child cart product collection from the cart ticket
     *
     *  If it does not exist a shop error is thrown
     */
    _getChildProductCollection(cartTicket, productGuid) {
        // noinspection SuspiciousTypeOfGuard
        if (typeof productGuid !== 'string' || !Object.prototype.hasOwnProperty.call(cartTicket.products.map, productGuid)) {
            throw new ShopError(`Product ${productGuid} not in cart ticket ${cartTicket.id}`);
        }
        return [cartTicket.products.collection, cartTicket.products.map[productGuid]];
    }
    /**
     *  Retrieve a ticket from the shop data if it exists.
     *
     *  If it does not exist a shop error is thrown
     */
    _getShopTicket(ticketGuid) {
        return this._getShopReservable(this._data.tickets_map, ticketGuid);
    }
    /**
     *  Retrieve a product from the shop data if it exists.
     *
     *  If it does not exist a shop error is thrown
     */
    _getShopProduct(productGuid) {
        return this._getShopReservable(this._data.products_map, productGuid);
    }
    /**
     *  Retrieve a reservable from the shop data if it exists.
     *
     *  If it does not exist a shop error is thrown
     */
    _getShopReservable(collection, guid) {
        // noinspection SuspiciousTypeOfGuard
        if (!guid || typeof guid !== 'string' || !Object.prototype.hasOwnProperty.call(collection, guid)) {
            throw new ShopError(`No valid item guid provided: ${guid}`);
        }
        const reservable = collection[guid];
        if (!reservable.guid || guid !== reservable.guid) {
            throw new ShopError(`Item does not have valid guid: ${reservable.guid}`);
        }
        if (!reservable.event_id || !Object.prototype.hasOwnProperty.call(this._data.events_map, reservable.event_id)) {
            throw new ShopError(`Item does not have valid event guid: ${reservable.event_id}`);
        }
        return reservable;
    }
    /**
     *  Retrieve a product from the ticket if it exists
     *
     *  If it does not exist a shop error is thrown
     */
    _getProductFromTicket(ticket, productGuid) {
        // noinspection SuspiciousTypeOfGuard
        if (typeof productGuid !== 'string' || !Object.prototype.hasOwnProperty.call(this._data.products_map, productGuid)) {
            throw new ShopError(`No valid product guid provided: ${productGuid}`);
        }
        // Make sure the product is part of the ticket
        const ticketProduct = ticket.products.all.find((ticketProduct) => {
            return ticketProduct.item.guid === productGuid;
        });
        if (!ticketProduct) {
            throw new ShopError(`Product ${productGuid} not part of ticket ${ticket.guid}`);
        }
        return ticketProduct;
    }
    /**
     *  Retrieve an event from the shop data if it exists.
     *
     *  If it does not exist a shop error is thrown
     */
    _getShopEvent(eventGuid) {
        // noinspection SuspiciousTypeOfGuard
        if (!eventGuid || typeof eventGuid !== 'string' || !Object.prototype.hasOwnProperty.call(this._data.events_map, eventGuid)) {
            throw new ShopError(`Event not found -> No valid event guid provided: ${eventGuid}`);
        }
        return this._data.events_map[eventGuid];
    }
    /**
     *  Ensure the given product is part of a global product collection in the shop data
     *
     *  Throws an error when not the case
     */
    _ensureGlobalProduct(productsCollection, product) {
        let found = false;
        forEach(productsCollection, (globalProduct) => {
            if (product.guid === globalProduct.guid) {
                found = true;
            }
            return !found;
        });
        if (found) {
            return product;
        }
        throw new ShopError(`Product not a global product: ${product.guid}`);
    }
    // noinspection JSMethodCanBeStatic
    /**
     *  Ensure the ticket is available.
     *
     *  This checks status and availability range.
     *
     *  @param ticket The ticket to ensure availability for.
     *  @param throwError If set to false, the method returns a boolean instead of throwing an error.
     */
    _ensureTicketAvailable(ticket, throwError = true) {
        let error = undefined;
        if (ticket.status_overrule === 'sold_out') {
            error = `Ticket is not available -> status overruled ${ticket.status_overrule}`;
        }
        else if (ticket.status !== 'available') {
            error = `Ticket is not available -> invalid status ${ticket.status}`;
        }
        else if (ticket.available_from && ticket.available_from > new Date()) {
            error = 'Ticket is not available -> Not available yet';
        }
        else if (ticket.available_until && ticket.available_until < new Date()) {
            error = 'Ticket is not available -> Not available any more';
        }
        if (error && throwError) {
            throw new ShopError(error);
        }
        return !error;
    }
    /**
     *  Ensure the event's item collection is defined
     */
    _getOrCreateEventCollection(eventGuid) {
        return this.items[eventGuid] = this.items[eventGuid] || {
            event: this._getShopEvent(eventGuid),
            tickets: {
                map: {},
                collection: [],
            },
            products: {
                map: {},
                collection: [],
            },
        };
    }
    /**
     *  Retrieve the event's cart ticket collection
     *
     *  This includes the complete ticket collection for the given event
     *  and the ticket map for the specific ticket.
     *
     *  If it does not exist a shop error is thrown
     */
    _getTicketItemCollection(eventGuid, ticketGuid) {
        if (!eventGuid || !Object.prototype.hasOwnProperty.call(this.items, eventGuid)) {
            throw new ShopError(`Event is not in cart: ${eventGuid}`);
        }
        const collection = this.items[eventGuid][CartItemChildType.Tickets];
        if (!ticketGuid || !Object.prototype.hasOwnProperty.call(collection.map, ticketGuid)) {
            throw new ShopError(`Ticket is not in cart: ${ticketGuid}`);
        }
        return [collection.collection, collection.map[ticketGuid]];
    }
    /**
     *  Retrieve the cart product collection from the event or the orphaned global products
     *
     *  If it does not exist a shop error is thrown
     */
    _getProductItemCollection(eventGuid, productGuid) {
        if (eventGuid && !Object.prototype.hasOwnProperty.call(this.items, eventGuid)) {
            throw new ShopError(`Event is not in cart: ${eventGuid}`);
        }
        const collection = eventGuid ? this.items[eventGuid][CartItemChildType.Products] : this.orphanedGlobalProducts;
        if (!productGuid || !Object.prototype.hasOwnProperty.call(collection.map, productGuid)) {
            throw new ShopError(`Product is not in cart: ${productGuid}`);
        }
        return [collection.collection, collection.map[productGuid]];
    }
    /**
     *  Cleanup the cart item's structure
     *
     *  When providing an event guid, ticket guid or product guid,
     *  the method can be focused to those paths.
     */
    _cleanUpItemStructure(eventGuid, ticketGuid, productGuid) {
        // When no eventGuid is provided, recursively call this method for each event in the collection
        if (!eventGuid) {
            const eventGuids = Object.keys(this.items);
            forEach(eventGuids, (eventGuid) => {
                this._cleanUpItemStructure(eventGuid);
            });
            return;
        }
        const eventCollection = this.items[eventGuid];
        if (!eventCollection) {
            return;
        }
        const ticketMap = eventCollection[CartItemChildType.Tickets].map;
        const productMap = eventCollection[CartItemChildType.Products].map;
        if (ticketGuid) {
            if (ticketMap[ticketGuid] && !Object.keys(ticketMap[ticketGuid]).length) {
                delete ticketMap[ticketGuid];
            }
        }
        else if (productGuid) {
            if (productMap[productGuid] && !Object.keys(productMap[productGuid]).length) {
                delete productMap[productGuid];
            }
        }
        else {
            // When no ticket guid and no product guid are provided, loop over all tickets and products
            const ticketGuids = Object.keys(eventCollection[CartItemChildType.Tickets].map);
            forEach(ticketGuids, (guid) => {
                if (ticketMap[guid] && !Object.keys(ticketMap[guid]).length) {
                    delete ticketMap[guid];
                }
            });
            const productGuids = Object.keys(eventCollection[CartItemChildType.Products].map);
            forEach(productGuids, (guid) => {
                if (productMap[guid] && !Object.keys(productMap[guid]).length) {
                    delete productMap[guid];
                }
            });
        }
        // Finally check if the entire event is now empty, if so, clean it up!
        if (!Object.keys(eventCollection[CartItemChildType.Tickets].map).length &&
            !Object.keys(eventCollection[CartItemChildType.Products].map).length) {
            delete this.items[eventGuid];
        }
    }
    /**
     *  Create the default cart item event object, to always return the same data.
     *
     *  @param item      - Reservable item
     *  @param cartItems - Array of the affected cart items
     *  @param type      - Type of cart item(s)
     *  @param parent    - Optional parent object (CartTicket)
     */
    _cartEventItemResponse(item, cartItems, type, parent) {
        let isTicket = false;
        const event = {
            guid: item.guid,
            name: item.name,
            items: cartItems.map((cartItem) => {
                if (cartItem.getType() === CartItemType.Ticket) {
                    isTicket = true;
                }
                return {
                    id: cartItem.id,
                    pricing: cartItem.pricing,
                };
            }),
            count: 0,
            counts: {},
            pricing: {
                base_price: 0,
                total_fees: 0,
                subtotal_price: 0,
                total_price: 0,
            },
        };
        // Forward the max orderable amount per order in case of a ticket
        if (isTicket) {
            event.max_count = item.max_orderable_amount_per_order;
        }
        let allCartItems;
        if (parent) {
            if (parent.products.collection.length > 0) {
                allCartItems = Object.values(parent.products.map[item.guid] || {});
            }
        }
        else {
            if (this.items[item.event_id] && this.items[item.event_id][type] && this.items[item.event_id][type].map[item.guid]) {
                allCartItems = Object.values(this.items[item.event_id][type].map[item.guid] || {});
            }
        }
        if (!allCartItems) {
            return event;
        }
        forEach(allCartItems, (cartItem) => {
            event.counts[cartItem.pricing.total_price] = (event.counts[cartItem.pricing.total_price] || 0) + 1;
        });
        event.count = allCartItems.length;
        event.pricing = Price.getCumulativeCartItemPrice(allCartItems);
        return event;
    }
    /**
     *  Used to create the default coupon response, including the products
     *  and tickets that got affected by the coupon action.
     */
    _cartEventCouponResponse(appliedCoupon, affected = []) {
        // Create the default coupon event
        const event = {
            guid: appliedCoupon ? appliedCoupon.guid : null,
            coupon: appliedCoupon ? appliedCoupon.item.coupon : null,
            code: appliedCoupon ? appliedCoupon.item.code : '',
            spend: appliedCoupon ? appliedCoupon.spend : 0,
            products: [],
            tickets: [],
        };
        if (affected.length) {
            // Used to make sure a ticket is only added to the affected tickets once,
            // as the coupon might have affected multiple products in a single ticket
            const affectedTickets = {};
            forEach(affected, (couponApplicable) => {
                event.products.push({
                    id: couponApplicable.product.id,
                    pricing: couponApplicable.product.pricing,
                });
                const cartTicket = couponApplicable.ticket;
                if (cartTicket && !affectedTickets[cartTicket.id]) {
                    event.tickets.push({
                        id: cartTicket.id,
                        pricing: cartTicket.pricing,
                    });
                    affectedTickets[cartTicket.id] = cartTicket.id;
                }
            });
        }
        return event;
    }
    /**
     *
     *  Remove all cart items from the cart
     *
     *  Internally release all reservations
     *
     *  Resolves with true on success
     *  Rejects with an array of all errors on failure
     *
     *  It waits for all actions to finish before the promise is settled.
     *  Failures of actions do NOT abort the rest. Every action is waited upon
     *
     *  Coupon stats are updated automatically because it uses the internal
     *  methods for removing tickets and/or products, which make sure to
     *  update coupon stats accordingly
     *
     */
    removeAllItems() {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                // Holding all remove promises
                const removePromises = [];
                // Collections of removed tickets and products per guid, so we are
                // able to create and emit a single event for every ticket and product
                // that got removed - because it might happen that a specific ticket or
                // product got added/removed multiple times
                const removedTickets = {};
                const removedProducts = {};
                forOwn(this.flatItems.tickets, (cartTicket) => {
                    // Ignore item if it is a child, as they will be handled by their parent(s)
                    if (cartTicket.childOf) {
                        return;
                    }
                    // Create the ticket remove promise and save the removed cart ticket for creating the single remove event below
                    const removeTicketPromise = this._removeTicket(cartTicket.item.guid, cartTicket.id, false).then(() => {
                        removedTickets[cartTicket.item.guid] = removedTickets[cartTicket.item.guid] || [];
                        removedTickets[cartTicket.item.guid].push(cartTicket);
                        return true;
                    }, (reason) => {
                        return Promise.resolve(reason);
                    });
                    removePromises.push(removeTicketPromise);
                });
                forOwn(this.flatItems.products, (cartProduct) => {
                    // Ignore item if it is a child, as they will be handled by their parent(s)
                    if (cartProduct.childOf) {
                        return;
                    }
                    // Create the product remove promise and save the removed cart product for creating the single remove event below
                    const removeProductPromise = this._removeProduct(cartProduct.item.guid, cartProduct.id, false).then(() => {
                        removedProducts[cartProduct.item.guid] = removedProducts[cartProduct.item.guid] || [];
                        removedProducts[cartProduct.item.guid].push(cartProduct);
                        return true;
                    }, (reason) => {
                        return Promise.resolve(reason);
                    });
                    removePromises.push(removeProductPromise);
                });
                const result = yield Promise.all(removePromises).then((results) => {
                    // Make sure to recompute everything after all remove promises are settled
                    this._itemsUpdated();
                    // Ends the action and emits the checkout details
                    this._action(CartAction.End);
                    // Create and emit a single remove event per ticket
                    forOwn(removedTickets, (removedTickets, ticketGuid) => {
                        const removedTicketsEvent = this._cartEventItemResponse(removedTickets[0].item, // The actual item of the ticket that got removed
                        removedTickets, CartItemChildType.Tickets);
                        this.emit(['ticket', ticketGuid, 'remove'], removedTicketsEvent);
                    });
                    // Create and emit a single remove event per ticket
                    forOwn(removedProducts, (removedProducts, productGuid) => {
                        const removedProductsEvent = this._cartEventItemResponse(removedProducts[0].item, // The actual item of the product that got removed
                        removedProducts, CartItemChildType.Products);
                        this.emit(['product', productGuid, 'remove'], removedProductsEvent);
                    });
                    // Check if one of the removes failed
                    const failures = results.filter((result) => result !== true);
                    if (failures.length) {
                        console.error(failures);
                        return Promise.reject(failures);
                    }
                    return true;
                });
                this.emit(['empty'], null);
                return result;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeAllItems', 'error', 'failed']);
            }
        });
    }
    // noinspection JSMethodCanBeStatic
    /**
     * Sanitize a coupon code by removing all whitespace
     */
    _sanitizeCouponCode(code) {
        return code.replace(/\s/g, '');
    }
    /**
     *  Used to apply a coupon. It makes sure to get the data of the coupon
     *  based on his code and apply the corresponding discount(s), but only
     *  when the coupon, or any other coupon, is not already applied.
     */
    addCoupon(code) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                const addedCouponEvent = yield this._addCoupon(code);
                // Only invoke the items updated method,
                // when products actually got affected.
                // This will also re-computes the checkout details.
                if (addedCouponEvent.products.length > 0) {
                    this._doneUpdatingItems();
                }
                this._action(CartAction.End);
                this.emit(['coupon', 'add', addedCouponEvent.code], addedCouponEvent);
                const tickets = {};
                forEach(addedCouponEvent.tickets, (item) => {
                    const cartTicket = this.flatItems.tickets[item.id];
                    tickets[cartTicket.item.guid] = tickets[cartTicket.item.guid] || { ticket: cartTicket.item, items: [] };
                    tickets[cartTicket.item.guid].items.push(cartTicket);
                });
                forOwn(tickets, (cartTickets) => {
                    this.emit(['ticket', cartTickets.ticket.guid, 'update'], this._cartEventItemResponse(cartTickets.ticket, cartTickets.items, CartItemChildType.Tickets));
                });
                const products = {};
                forEach(addedCouponEvent.products, (item) => {
                    const cartProduct = this.flatItems.products[item.id];
                    products[cartProduct.item.guid] = products[cartProduct.item.guid] || { product: cartProduct.item, items: [] };
                    products[cartProduct.item.guid].items.push(cartProduct);
                });
                forOwn(products, (cartProducts) => {
                    this.emit(['product', cartProducts.product.guid, 'update'], this._cartEventItemResponse(cartProducts.product, cartProducts.items, CartItemChildType.Products));
                });
                return addedCouponEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addCoupon', 'error', 'failed'], { code });
            }
        });
    }
    /**
     *  Internally used to apply a coupon. It makes sure to get the data of the coupon
     *  based on his code and apply the corresponding discount(s), but only
     *  when the coupon, or any other coupon, is not already applied.
     */
    _addCoupon(code) {
        return __awaiter(this, void 0, void 0, function* () {
            // noinspection SuspiciousTypeOfGuard
            if (typeof code !== 'string' || !code.length) {
                throw new ShopError(`Cannot add coupon -> The provided code is invalid: ${code}`);
            }
            code = this._sanitizeCouponCode(code);
            const appliedCoupons = Object.keys(this._coupons);
            if (appliedCoupons.length) {
                const appliedCoupon = this.getAppliedCoupon(code);
                // Just return the data of the coupon if it has already been applied
                // with the number of affected items as zero - because no items
                // got affected because it was already applied
                if (appliedCoupon) {
                    return this._cartEventCouponResponse(appliedCoupon);
                }
                // The current setup only supports the applying of a single coupon
                // (!) Remove when multiple coupons are supported
                throw this.log._create(LogLevel.Error, ['cart', 'addCoupon', 'error', 'multiple'], {
                    code,
                    existing_code: this._coupons[appliedCoupons[0]].item.code,
                });
            }
            let res;
            try {
                const url = this._config.getNoCacheUrl(`${this._config.guid}/coupon/${code}`);
                res = yield this._config.httpClient.get(url);
            }
            catch (error) {
                if (error && error.response) {
                    if (error.response.status === 404) {
                        throw this.log._create(LogLevel.Warning, ['cart', 'addCoupon', 'error', 'notfound'], { code }, null, error, false);
                    }
                    else if (error.response.status === 406) {
                        throw this.log._create(LogLevel.Warning, ['cart', 'addCoupon', 'error', 'notacceptable'], { code }, null, error, false);
                    }
                }
                throw this.log._create(LogLevel.Warning, ['cart', 'addCoupon', 'error', 'failed'], { code }, null, error);
            }
            const coupon = res.data;
            // Add the guid to it's inner data, because only the inner data is
            // returned that will be returned in the event
            coupon.coupon.guid = coupon.guid;
            // Create the applied coupon object for the added coupon
            const appliedCoupon = {
                guid: coupon.guid,
                item: coupon,
                appliedCount: 0,
                appliesTo: {
                    cart: this._getCouponApplicableItems(coupon),
                    shop: [],
                },
                spend: 0,
            };
            // Add the coupon discounts, which returns the applicable items that actually got affected
            const affectedItems = this._couponDiscounts(CouponAction.Add, appliedCoupon);
            // Also update the coupon discounts in the shop
            this._shop._updateCouponDiscounts(CouponAction.Add, appliedCoupon);
            // Save it to the cart's coupons by guid, because a code
            // is unique but might also contain special chars
            this._coupons[coupon.guid] = appliedCoupon;
            // Return the data of the just added coupon and the number of items
            // that got affected, which is equal to the applied count at this point
            return this._cartEventCouponResponse(appliedCoupon, affectedItems);
        });
    }
    /**
     *  Used to add multiple coupons, based on an Array of coupon codes.
     */
    addCoupons(codes) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                const addCouponEvents = yield this._addCoupons(codes);
                // Ends the action and emits the checkout details
                this._action(CartAction.End);
                return addCouponEvents;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'addCoupons', 'error', 'failed'], { codes });
            }
        });
    }
    /**
     *  Used to add multiple coupons, based on an Array of coupon codes.
     */
    _addCoupons(codes) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(codes)) {
                throw new ShopError(`Cannot add coupons -> The provided codes is not an Array: ${codes}`);
            }
            if (!codes.length) {
                throw new ShopError('Cannot add coupons -> The provided codes Array is empty');
            }
            codes = codes.map((code) => this._sanitizeCouponCode(code));
            // Save every event for adding coupons
            const addCouponEvents = [];
            // Check if any items got affected by the coupons
            let affectedItems = false;
            // Add every coupon code
            for (const code of codes) {
                const addedCouponEvent = yield this._addCoupon(code);
                this.emit(['coupon', 'add', code], addedCouponEvent);
                if (addedCouponEvent.products.length > 0) {
                    affectedItems = true;
                }
                addCouponEvents.push(addedCouponEvent);
            }
            // Only invoke the items updated method - which re-computes
            // the checkout details - when items actually got
            // affected, otherwise just end the action
            if (affectedItems) {
                this._itemsUpdated();
            }
            return addCouponEvents;
        });
    }
    /**
     *
     *  Used to remove the coupon that belongs to the forwarded code.
     *
     *  @return remove coupon events that holds the data of the coupon
     *  that got removed or null if no coupon was applied for the code
     *
     */
    removeCoupon(code) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                const removeCouponEvent = yield this._removeCoupon(code);
                // Only invoke the items updated method,
                // when products actually got affected.
                // This will also re-computes the checkout details.
                if (removeCouponEvent.products.length > 0) {
                    this._doneUpdatingItems();
                }
                this._action(CartAction.End);
                this.emit(['coupon', 'remove', removeCouponEvent.code], removeCouponEvent);
                const tickets = {};
                forEach(removeCouponEvent.tickets, (item) => {
                    const cartTicket = this.flatItems.tickets[item.id];
                    tickets[cartTicket.item.guid] = tickets[cartTicket.item.guid] || { ticket: cartTicket.item, items: [] };
                    tickets[cartTicket.item.guid].items.push(cartTicket);
                });
                forOwn(tickets, (cartTickets) => {
                    this.emit(['ticket', cartTickets.ticket.guid, 'update'], this._cartEventItemResponse(cartTickets.ticket, cartTickets.items, CartItemChildType.Tickets));
                });
                const products = {};
                forEach(removeCouponEvent.products, (item) => {
                    const cartProduct = this.flatItems.products[item.id];
                    products[cartProduct.item.guid] = products[cartProduct.item.guid] || { product: cartProduct.item, items: [] };
                    products[cartProduct.item.guid].items.push(cartProduct);
                });
                forOwn(products, (cartProducts) => {
                    this.emit(['product', cartProducts.product.guid, 'update'], this._cartEventItemResponse(cartProducts.product, cartProducts.items, CartItemChildType.Products));
                });
                return removeCouponEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeCoupon', 'error', 'failed'], { code });
            }
        });
    }
    /**
     *  Internally used to remove the coupon that belongs to the forwarded code.
     */
    _removeCoupon(code) {
        return __awaiter(this, void 0, void 0, function* () {
            // noinspection SuspiciousTypeOfGuard
            if (typeof code !== 'string' || !code.length) {
                throw new ShopError(`Cannot remove coupon -> The provided code is invalid: ${code}`);
            }
            code = this._sanitizeCouponCode(code);
            let appliedCoupon = this.getAppliedCoupon(code);
            if (!appliedCoupon) {
                return this._cartEventCouponResponse(null);
            }
            // Remove the coupon discounts, which returns the applicable items that actually got affected
            const affectedItems = this._couponDiscounts(CouponAction.Remove, appliedCoupon);
            // Also update the coupon discounts in the shop
            this._shop._updateCouponDiscounts(CouponAction.Remove, appliedCoupon);
            // Delete the coupon after the discounts have been removed
            delete this._coupons[appliedCoupon.item.guid];
            // Return the event with the affected items
            return this._cartEventCouponResponse(appliedCoupon, affectedItems);
        });
    }
    /**
     *  Used to remove multiple coupons, based on an Array of coupon codes.
     */
    removeCoupons(codes) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                const removeCouponEvents = yield this._removeCoupons(codes);
                this._action(CartAction.End);
                return removeCouponEvents;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'removeCoupons', 'error', 'failed'], { codes });
            }
        });
    }
    /**
     *  Used to remove multiple coupons, based on an Array of coupon codes.
     */
    _removeCoupons(codes) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(codes)) {
                throw new ShopError(`Cannot remove coupons -> The provided codes is not an Array: ${codes}`);
            }
            if (!codes.length) {
                throw new ShopError('Cannot remove coupons -> The provided codes Array is empty');
            }
            codes = codes.map((code) => this._sanitizeCouponCode(code));
            // Save every event for removing coupons
            const removeCouponEvents = [];
            // Check if any items got affected by the coupons
            let affectedItems = false;
            // Remove every coupon code
            for (const code of codes) {
                const removeCouponEvent = yield this._removeCoupon(code);
                this.emit(['coupon', 'remove', code], removeCouponEvent);
                if (removeCouponEvent.products.length > 0) {
                    affectedItems = true;
                }
                removeCouponEvents.push(removeCouponEvent);
            }
            // Only invoke the items updated method - which ends the action including
            // a re-computation of the checkout details - when items actually got
            // affected, otherwise just end the action
            if (affectedItems) {
                this._itemsUpdated();
            }
            return removeCouponEvents;
        });
    }
    /**
     *  Used to get the data of an applied coupon, based on his code.
     */
    getAppliedCoupon(code) {
        let appliedCoupon = undefined;
        forOwn(this._coupons, (ac) => {
            if (ac.item.code === code) {
                appliedCoupon = ac;
            }
            return !appliedCoupon;
        });
        return appliedCoupon;
    }
    /**
     *  Used to get the data of all applied coupons.
     */
    getAppliedCoupons() {
        return this._coupons;
    }
    /**
     *  Set the payment method for this order.
     *
     *  Optionally an issuer id can be provided to immediately set an issuer.
     *
     *  If the payment method is already set and equals the provided guid,
     *  the item within the cart payment method is replaced,
     *  but the cart payment method object is NOT replaced.
     *  Because of this, this method can also be used to only update the issuer.
     *
     *  ```ts
     *  const cartPaymentMethod: CartPaymentMethod = cart.setPaymentMethod(paymentMethod.guid);
     *  const cartPaymentMethod: CartPaymentMethod = cart.setPaymentMethod(paymentMethod.guid, paymentMethod.issuers[0].id);
     *  ```
     *
     *  | Error                                    | Reason                                                                                 |
     *  | :--------------------------------------- | :------------------------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid payment method guid in shop data [ShopData](../interfaces/shopdata.html) |
     *  |                                          | No or invalid issuer id in payment method [ShopData](../interfaces/shopdata.html)      |
     */
    setPaymentMethod(paymentMethodGuid, issuerId) {
        return __awaiter(this, void 0, void 0, function* () {
            this._action(CartAction.Start);
            try {
                const setPaymentMethodEvent = yield this._setPaymentMethod(paymentMethodGuid, issuerId);
                this._action(CartAction.End);
                this.emit(['payment_method', 'set', setPaymentMethodEvent.guid], setPaymentMethodEvent);
                return setPaymentMethodEvent;
            }
            catch (error) {
                this._action(CartAction.End);
                throw this.log._passThroughOrCreate(error, LogLevel.Error, ['cart', 'setPaymentMethod', 'error', 'failed'], { paymentMethodGuid, issuerId });
            }
        });
    }
    /**
     *
     *  Internally used for setting the payment method for this order.
     *
     *  @param paymentMethodGuid The guid of the payment method to set.
     *  @param issuerId When a payment method has issuers, this is where to set them.
     *
     */
    _setPaymentMethod(paymentMethodGuid, issuerId) {
        return __awaiter(this, void 0, void 0, function* () {
            // noinspection SuspiciousTypeOfGuard
            if (typeof paymentMethodGuid !== 'string' || !Object.prototype.hasOwnProperty.call(this._data.payment_methods_map, paymentMethodGuid)) {
                throw new ShopError(`Cannot set payment method -> No valid payment method guid provided: ${paymentMethodGuid}`);
            }
            const paymentMethod = this._data.payment_methods_map[paymentMethodGuid];
            if (!paymentMethod.guid || paymentMethodGuid !== paymentMethod.guid) {
                throw new ShopError(`Cannot set payment method -> Payment method does not have valid guid: ${paymentMethod.guid}`);
            }
            let issuer;
            if (issuerId) {
                issuer = paymentMethod.issuers.find((issuer) => {
                    return issuer.id === issuerId;
                });
                if (!issuer) {
                    throw new ShopError(`Cannot set payment method -> Issuer ${issuerId} not part of payment method ${paymentMethod.guid}`);
                }
            }
            else if (this.paymentMethod && this.paymentMethod.item.guid === paymentMethod.guid) {
                // This is to ensure we don't remove the issuer when the payment method is the same as the previous
                issuer = this.paymentMethod.issuer;
            }
            this.paymentMethod.item = paymentMethod;
            this.paymentMethod.issuer = issuer;
            this._computeCheckoutDetails();
            return {
                guid: paymentMethod.guid,
                name: paymentMethod.name,
                issuer: issuer,
                pricing: paymentMethod.pricing,
            };
        });
    }
    /**
     *  Mount the stripe payment method elements.
     *
     *  Optionally card options can be provided.
     *  These options are primarily used to style the input elements.
     *
     *  If the stripe card is already set,
     *  the card is mounted (again), but the card object is NOT replaced.
     *
     *  | Error                                    | Reason                          |
     *  | :--------------------------------------- | :------------------------------ |
     *  | [ShopError](shoperror.html)              | Invalid payment method selected |
     */
    mountStripePaymentMethod(selector, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.paymentMethod.item.stripe || !this.paymentMethod.item.stripe.stripe) {
                throw this.log._create(LogLevel.Error, ['cart', 'mountStripePaymentMethod', 'error', 'initialized']);
            }
            const paymentMethod = this.paymentMethod;
            const stripe = paymentMethod.item.stripe;
            if (!stripe.card) {
                // Create an instance of Elements.
                const elements = stripe.stripe.elements();
                // Create an instance of the card Element.
                stripe.card = elements.create('card', options);
                stripe.card.on('change', (event) => {
                    stripe.errors.splice(0, stripe.errors.length);
                    stripe.complete = false;
                    if (event.complete) {
                        // enable payment button
                        stripe.complete = true;
                        this.emit(['payment_method', 'change', paymentMethod.item.guid, 'stripe', 'complete'], true);
                    }
                    else if (event.error) {
                        // show validation to customer
                        stripe.errors.push(event.error);
                        this.emit(['payment_method', 'change', paymentMethod.item.guid, 'stripe', 'error'], event.error);
                    }
                });
            }
            return new Promise((resolve, reject) => {
                const timeout = window.setTimeout(reject, 15000);
                stripe.card.on('ready', () => {
                    this.emit(['payment_method', 'change', paymentMethod.item.guid, 'stripe', 'ready'], true);
                    window.clearTimeout(timeout);
                    resolve();
                });
                stripe.card.mount(selector);
            });
        });
    }
    /**
     *  Finalize the payment method if applicable
     *
     *  | Error                                    | Reason                                      |
     *  | :--------------------------------------- | :------------------------------------------ |
     *  | [ShopError](shoperror.html)              | Invalid payment method. E.g.: Stripe failed |
     */
    finalizePaymentMethod() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.paymentMethod.item.stripe && this.paymentMethod.item.stripe.stripe && this.paymentMethod.item.stripe.card) {
                const stripeTokenResponse = yield this.paymentMethod.item.stripe.stripe.createToken(this.paymentMethod.item.stripe.card);
                if (stripeTokenResponse.error) {
                    this.paymentMethod.errors = [stripeTokenResponse.error];
                    throw this.log._create(LogLevel.Error, ['cart', 'finalizePaymentMethod', 'error', 'stripe', 'error'], stripeTokenResponse.error);
                }
                else if (!stripeTokenResponse.token) {
                    throw this.log._create(LogLevel.Error, ['cart', 'finalizePaymentMethod', 'error', 'stripe', 'token'], stripeTokenResponse.error);
                }
                this.paymentMethod.stripe_token = stripeTokenResponse.token;
            }
        });
    }
    /**
     *  Agree to a specific or default terms.
     *
     *  Rejecting terms is only possible for named terms with the second argument
     *
     *  ```ts
     *  const terms: { [key: string]: boolean } = cart.agreeToTerms();
     *  const terms: { [key: string]: boolean } = cart.agreeToTerms('default');
     *  const terms: { [key: string]: boolean } = cart.agreeToTerms('default', true);
     *  const terms: { [key: string]: boolean } = cart.agreeToTerms('default', false);
     *  ```
     *
     *  | Error                                    | Reason                                                                  |
     *  | :--------------------------------------- | :---------------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | No or invalid terms id provided [ShopData](../interfaces/shopdata.html) |
     */
    agreeToTerms(termId = 'default', agree = true) {
        return __awaiter(this, void 0, void 0, function* () {
            // noinspection SuspiciousTypeOfGuard
            if (typeof termId !== 'string' || !Object.prototype.hasOwnProperty.call(this.terms, termId)) {
                throw this.log._create(LogLevel.Error, ['cart', 'agreeToTerms', 'error', 'terms'], { termId, agree });
            }
            this.terms[termId] = [true, 1, '1', 'true'].indexOf(agree) > -1;
            const action = agree ? TermsActions.Accept : TermsActions.Reject;
            this.emit(['terms', action, termId], this.terms);
            return this.terms;
        });
    }
    /**
     *  Set the locale for the order.
     *
     *  This value will be send to the API.
     *  E.g.: tickets can be rendered using the correct locale.
     *
     *  ```ts
     *  cart.setLocale('en_GB');
     *  ```
     */
    setLocale(locale) {
        this.locale = locale;
        setValidationLang(locale);
        this._resetPriceFormatter();
        this.emit(['locale'], locale);
    }
    /**
     *  Update the price formatter
     *
     *  This method can be called when i.e. the locale changes
     */
    _resetPriceFormatter() {
        const tryLocales = [
            this.locale,
            this.locale.replace('_', '-'),
            this.locale.substr(0, 2),
            'en-GB',
        ];
        const localeStructures = Intl.NumberFormat.supportedLocalesOf(tryLocales.filter((locale) => {
            try {
                Intl.NumberFormat.supportedLocalesOf(locale);
                return true;
            }
            catch (e) {
                return false;
            }
        }));
        return this._priceFormatter = new Intl.NumberFormat(localeStructures, { style: 'currency', currency: this._data.currency || 'EUR' });
    }
    /**
     *  Set extra arbitrary data to add to the place order call
     */
    setTechData(techData) {
        this.techData = Object.assign(Object.assign({}, techData), this.techData);
    }
    /**
     * Set the booker information and metadata from the outside
     * @param data - the booker information
     */
    setBookerInformation(data) {
        // TS does not allow for using a loop here, as the properties are not part
        // of a map definition
        if (data.firstname) {
            this.booker.firstname.value = data.firstname;
        }
        if (data.lastname) {
            this.booker.lastname.value = data.lastname;
        }
        if (data.email) {
            this.booker.email.value = data.email;
        }
        // Also set the dynamic metadata values, if provided
        for (const metadata of Object.values(this.metadata)) {
            const value = data[metadata.item.name] || data[metadata.item.auto_fill_facebook];
            if (value !== undefined && value !== null) {
                metadata.value = value;
            }
        }
    }
    /**
     *  Send the order to the API.
     *
     *  Before the request will be attempted, the cart structure and values will be validated.
     *  If validation fails, a ShopError will be thrown.
     *
     *  The cart content will then be formatted to the data format the API expects,
     *  before sending the order to the API.
     *
     *  ```ts
     *  const orderResponse: PlaceOrderResponse = await cart.placeOrder();
     *  ```
     *
     *  | Error                                    | Reason                                                          |
     *  | :--------------------------------------- | :-------------------------------------------------------------- |
     *  | [ShopError](shoperror.html)              | A validation failure is encountered                             |
     *  | [OrderApiError](orderapierror.html)      | The API rejects the data, some precondition or validation fails |
     *  | [ApiError](apierror.html)                | The API encountered an unexpected error                         |
     */
    placeOrder() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield this._placeOrder();
                this.emit(['order', 'placed'], response); // TODO @Rik decide on path/payload
                // TODO @Rik Should we transform the response here?
                return response;
            }
            catch (error) {
                throw this.log._passThroughOrCreate(error, LogLevel.Fatal, ['cart', 'placeOrder', 'error', 'failed']);
            }
        });
    }
    _placeOrder() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.checkout_details.busy) {
                throw this.log._create(LogLevel.Debug, ['cart', 'placeOrder', 'error', 'busy']);
            }
            const validation = this.validator.all();
            if (!validation.valid) {
                throw this.log._create(LogLevel.Warning, ['cart', 'placeOrder', 'error', 'validation'], { errors: validation.errors });
            }
            const seatsOptions = this.seatsioInstance && this.seatsioInstance.holdToken
                ? { holdToken: this.seatsioInstance.holdToken }
                : undefined;
            const ticketOptions = Object.values(this.flatItems[CartItemChildType.Tickets]);
            const productOptions = Object.values(this.flatItems[CartItemChildType.Products]).filter((cartProduct) => !cartProduct.childOf);
            const metadataOptions = Object.values(this.metadata);
            const couponOptions = Object.values(this._coupons);
            const orderbuilderOptions = {
                // Required, static booker info
                booker: {
                    bookerInfo: this.booker,
                    locale: this.locale,
                },
                // The tickets and global products
                // Tickets have nested products
                // Tickets and (global) products have nested metadata
                tickets: ticketOptions,
                products: productOptions,
                // Dynamic booker info / shop level metadata
                metaData: metadataOptions,
                // Shop level seats info (like hold token, used to verify/book the seats from the API)
                seats: seatsOptions,
                // Chosen payment method and needed info
                paymentMethod: this.paymentMethod,
                // Applied coupon(s), note: currently only one coupon supported.
                coupons: couponOptions,
                cartInitAt: this._initAt,
                extraTechData: this.techData,
            };
            return yield this._orderbuilder.handle(orderbuilderOptions);
        });
    }
    // noinspection JSUnusedGlobalSymbols
    /**
     *  Listener for changes on the checkout details
     */
    onCheckoutDetails(cb) {
        // TODO @Rik Is this method actually used??
        //  It seems a bit useless as the other events do not have a quick access like this?
        //  Maybe add something like this to the event emitter constructor??
        //  `this.on.prototype.checkoutDetails = (cb: EventCallback) => eventEmitter.on.call(eventEmitter, [ 'checkout' ], cb);`
        //  That way we could call `cart.on.checkoutDetails(() => {})`, without bloating the cart ??
        this.on(['checkout'], cb);
    }
    /**
     *  Remove any metadata no longer used by the global products in the cart.
     */
    _checkRemovedProductMetadata(metadataCollection) {
        const metadataRemove = {};
        forEach(metadataCollection, (metadata) => {
            if (metadata.applies_to !== 'person') {
                return;
            }
            metadataRemove[metadata.guid] = metadata;
        });
        // If there are multiple (active) roads to Rome, Rome should not be destroyed.
        // In this case, the shop-metadata pivot
        forEach(this._data.booker_metadata, (metadata) => {
            delete metadataRemove[metadata.guid];
        });
        // In this case other products.
        forOwn(this.flatItems[CartItemChildType.Products], (cartProduct) => {
            // Ignore ticket products
            if (cartProduct.childOf) {
                return;
            }
            forEach(cartProduct.item.metadata, (metadata) => {
                // Ignore product metadata
                if (metadata.applies_to !== 'person') {
                    return;
                }
                delete metadataRemove[metadata.guid];
            });
        });
        // Actually remove the metadata from the cart
        forEach(Object.keys(metadataRemove), (metadataId) => {
            delete this.metadata[metadataId];
        });
        // TODO Emit ???
    }
    /**
     *  Update the reservation interval
     *
     *  This creates an interval if one does not exist yet and there are items in the cart.
     *  This clears the interval if one already exists and there are no items in the cart (anymore).
     *
     *  This method can be called with a cart item id that will be ignored (e.g. an item that will be removed).
     *  However note that right now this does not ignore it's child items.
     *
     *  When setting a new interval,
     *  the expiration date is set and the interval is started to update every 100ms.
     *  The interval updates depending on the current time,
     *  so it won't fluctuate depending on interval trigger rates of the client.
     *  The interval is therefore triggered every 100 milliseconds to ensure the reservation time is not jittery.
     */
    _updateReservationTimer(removingCartItemId) {
        const hasTickets = 0 < Object.keys(this.flatItems.tickets).filter((cartItemId) => cartItemId !== removingCartItemId).length;
        const hasProducts = 0 < Object.keys(this.flatItems.products).filter((cartItemId) => cartItemId !== removingCartItemId).length;
        const hasItems = hasTickets || hasProducts;
        const reservationTickerHandle = this._reservationTicker;
        const reservationTimeMs = this._reservationTime * 60000;
        if (hasItems && this._reservationTime && !reservationTickerHandle) {
            this.reservationExpiration.date = new Date(Date.now() + reservationTimeMs);
            this._updateRelativeReservationExpirationValues();
            this._reservationTicker = window.setInterval(this._updateRelativeReservationExpirationValues.bind(this), 100);
            window.clearInterval(reservationTickerHandle);
            this.emit(['timer', 'start'], this.reservationExpiration, true);
        }
        else if (reservationTickerHandle && !hasItems) {
            // noinspection SuspiciousTypeOfGuard
            const isTimeout = typeof this.reservationExpiration.secondsRemaining === 'number' && this.reservationExpiration.secondsRemaining <= 0;
            this.reservationExpiration.date = null;
            this.reservationExpiration.secondsRemaining = null;
            this._reservationTicker = undefined;
            window.clearInterval(reservationTickerHandle);
            this.emit(['timer', 'clear'], Object.assign(Object.assign({}, this.reservationExpiration), { timeout: isTimeout }), true);
        }
    }
    /**
     *  Update the relative reservation expiration value
     *
     *  This is mostly called by the reservation interval (every 100 milliseconds)
     *
     *  The seconds remaining value is updates depending on the current time,
     *  so it won't fluctuate depending on interval trigger rates of the client.
     *  When the seconds remaining value hits zero, the interval (if it exists) is cleared,
     *  the values reset and all cart items removed (reservations released).
     */
    _updateRelativeReservationExpirationValues() {
        if (!this.reservationExpiration.date) {
            throw slugError('Invalid reservation expiration date value', ['cart', '_updateRelativeReservationExpirationValues', 'error', 'date']);
        }
        this.reservationExpiration.secondsRemaining = Math.max(0, Math.floor((this.reservationExpiration.date.getTime() - Date.now()) / 1000));
        this.emit(['timer', 'tick'], this.reservationExpiration, true);
        if (this.reservationExpiration.secondsRemaining < 1) {
            // noinspection JSIgnoredPromiseFromCall
            this.removeAllItems();
            this._updateReservationTimer();
        }
    }
    /**
     *  Used to get the cart items to which a specific forwarded coupon
     *  applies, making use of the coupon logic.
     *
     *  @return a sorted Array of items on which the forwarded coupon
     *  applies, descending from the most expensive
     */
    _getCouponApplicableItems(coupon) {
        /**
         *  Used to get the cart ticket of the cart product to which the coupon
         *  applies, as used by the Coupon.getApplicableItems method.
         *
         *  (i) It doesn't have a ticket when it's about a global product
         */
        const getTicketOfCartProduct = (cartProduct) => {
            return cartProduct.childOf
                ? this.flatItems.tickets[cartProduct.childOf]
                : undefined;
        };
        return Coupon.getApplicableItems(coupon, this.flatItems.products, getTicketOfCartProduct);
    }
    /**
     *
     *  Used for adding or removing the discount of a coupon on all applicable
     *  items, taking the applies_to_count and amount_override into account.
     *
     *  @return The affected coupon applicable items.
     *
     */
    _couponDiscounts(action, coupon) {
        const affectedApplicableItems = [];
        if (!coupon) {
            return [];
        }
        if (!enumIncludes(CouponType, coupon.item.coupon.type)) {
            throw new ShopError(`Cannot ${action} coupon -> Unknown coupon type: ${coupon.item.coupon.type}`);
        }
        if (!coupon.appliesTo.cart.length) {
            return [];
        }
        // Do not start applying the discounts when the max has been reached, or do
        // not start removing discounts when it was not applied to any products
        if (action === CouponAction.Add && coupon.appliedCount === coupon.item.applies_to_count ||
            action === CouponAction.Update && coupon.appliedCount === coupon.item.applies_to_count ||
            action === CouponAction.Remove && coupon.appliedCount === 0) {
            return [];
        }
        forEach(coupon.appliesTo.cart, (couponApplicable) => {
            Coupon.applyDiscount(coupon, action, couponApplicable);
            affectedApplicableItems.push(couponApplicable);
            // Break the adding of discounts when the max has been reached, or break
            // the removing of discounts if all applied discounts were removed
            return action === CouponAction.Remove
                ? !(coupon.appliedCount === 0)
                : !(coupon.appliedCount === coupon.item.applies_to_count);
        });
        return affectedApplicableItems;
    }
    /**
     *  Used to check if a coupon was applied on a product that got removed,
     *  making sure to lower the applied count and the spend amount of the
     *  coupon if so - keeping the stats of an applied coupon up to date.
     */
    _checkRemovedProductCoupons(cartProduct) {
        forOwn(cartProduct.pricing.coupons, (delta, guid) => {
            this._coupons[guid].appliedCount--;
            this._coupons[guid].spend -= delta;
        });
    }
    /**
     *
     *  Used to make sure the discount of a coupon has been distributed correctly
     *  among the coupon applicable products, making sure it's still correct
     *  when a product got added or removed.
     *
     *  (!) The re-computation of the prices is invoked in the _itemsUpdated()
     *  method, because it should have invoked this method for every applied
     *  coupon before recomputing the prices.
     *
     */
    _checkCouponDiscounts(coupon) {
        // Because the method for getting the coupon applicable items sorts them,
        // the products on which it should be applied first are on top
        coupon.appliesTo.cart = this._getCouponApplicableItems(coupon.item);
        forEach(coupon.appliesTo.cart, (couponApplicable, index) => {
            const applicable = (index + 1) <= coupon.item.applies_to_count;
            const applied = typeof couponApplicable.product.pricing.coupons[coupon.item.guid] === 'number';
            if (applicable) {
                if (!applied) {
                    Coupon.applyDiscount(coupon, CouponAction.Add, couponApplicable);
                }
            }
            else if (applied) {
                Coupon.applyDiscount(coupon, CouponAction.Remove, couponApplicable);
            }
        });
        this._shop._updateCouponDiscounts(CouponAction.Update, coupon);
        this.emit(['coupon', CouponAction.Update, coupon.item.code], this._cartEventCouponResponse(coupon));
    }
    /**
     *  Used to reset an applied coupon; resetting his applied count and
     *  amount spend and recomputing the applicable items for the cart.
     */
    _resetCoupon(coupon) {
        coupon.appliedCount = 0;
        coupon.spend = 0;
        coupon.appliesTo.cart = this._getCouponApplicableItems(coupon.item);
    }
    /**
     *  Used to notify the cart if an action (adding a ticket, adding a product) started or ended,
     *  so the checkout details / cart can become pending while actions are pending and we
     *  can block the place order call if actions are pending.
     */
    _action(action) {
        // Update the count of pending actions accordingly
        this._pendingActions += action === CartAction.Start ? 1 : -1;
        // There might be multiple actions pending
        this.checkout_details.busy = (this._pendingActions > 0);
        // Always emit the checkout details when an action is triggered
        this._emitCheckoutDetails();
    }
    /**
     *  Invoked every time the items in the cart updated. It
     *  updates the reservation timer accordingly, checks the
     *  coupons, recomputes the pricing of all items in the cart
     *  and builds and emits the checkout details.
     */
    _itemsUpdated() {
        /**
         *  The reservation time should not change when removing child reservations
         *  [_addProductToTicket() and _removeProductFromTicket()]. However, in case
         *  this ever changes in the future, the update method is called here anyway.
         */
        this._updateReservationTimer();
        // Check the coupon discounts
        forOwn(this._coupons, (coupon) => {
            this._checkCouponDiscounts(coupon);
        });
        // Recomputes the cart item pricing and
        // recomputes and emit the checkout details
        this._doneUpdatingItems();
    }
    /**
     *  Used to compute the actual (new) pricing of products and tickets in the cart when
     *  the updating of items is done taking any applied coupons into account, as discounts
     *  are applied to the pricing object of the item immediately.
     */
    _doneUpdatingItems() {
        // Used by the Price's computeItemsPricing method to collect the
        // products of the ticket for which it is computing the pricing
        const getProductsOfTicket = (cartTicket) => {
            return cartTicket.products.collection;
        };
        Price.computeItemsPricing(this.flatItems.products, this.flatItems.tickets, getProductsOfTicket);
        this._computeCheckoutDetails();
    }
    /**
     *  Used to compute the checkout details of the cart based
     *  on his current items, taking tickets and both regular
     *  as container products into account.
     */
    _computeCheckoutDetails() {
        // Always compute the checkout details from zero
        const checkoutDetails = {
            vats: {
                percentages: {},
                total: 0,
            },
            subtotal_price: 0,
            payment_method_fee: 0,
            total_price: 0,
            // Actual pending is being set below to prevent race conditions
            busy: true,
        };
        // Save global container products for adding their pricing
        // last, as they apply on the order in its entirety
        const containerProducts = [];
        // Add the numbers of every ticket that currently is in the cart
        forOwn(this.flatItems.tickets, (cartTicket) => {
            checkoutDetails.total_price += cartTicket.pricing.total_price;
            checkoutDetails.subtotal_price += cartTicket.pricing.subtotal_price;
            forOwn(cartTicket.pricing.including_vats.percentages, (amount, percentage) => {
                Price.updateVATCollection(VATUpdateAction.Add, checkoutDetails.vats, percentage, amount);
            });
        });
        // Add the numbers of every global product that currently is in the cart
        forOwn(this.flatItems.products, (cartProduct) => {
            // A global product does not have a parent
            if (cartProduct.childOf !== undefined) {
                return;
            }
            // Save the (global) container products for adding last
            if (cartProduct.item.pricing_method === PricingMethod.Container) {
                containerProducts.push(cartProduct);
                return;
            }
            checkoutDetails.total_price += cartProduct.pricing.total_price;
            checkoutDetails.subtotal_price += cartProduct.pricing.subtotal_price;
            Price.updateVATCollection(VATUpdateAction.Add, checkoutDetails.vats, cartProduct.pricing.vat.percentage, cartProduct.pricing.vat.amount);
        });
        // Add the container products, of which the pricing applies to the total order price
        if (containerProducts.length) {
            const originalOrderPrice = checkoutDetails.total_price;
            forEach(containerProducts, (cartProduct) => {
                // Recompute the total prices of the product based on his container price (the order)
                // (i) It's ok if it becomes zero because the order price is zero
                Price.computeProductPricing(cartProduct.pricing, originalOrderPrice);
                // Add his newly computed pricing to the checkout details
                checkoutDetails.total_price += cartProduct.pricing.total_price;
                checkoutDetails.subtotal_price += cartProduct.pricing.subtotal_price;
                Price.updateVATCollection(VATUpdateAction.Add, checkoutDetails.vats, cartProduct.pricing.vat.percentage, cartProduct.pricing.vat.amount);
            });
        }
        // Always (re)compute the payment method fees, as it will
        // also reset them to zero if the cart became empty
        this._computePaymentMethodFees(checkoutDetails);
        // Add the fees of a payment method if there is something to pay
        // for in the cart and a payment method has been set
        if (checkoutDetails.total_price > 0 && this.paymentMethod) {
            checkoutDetails.payment_method_fee = this.paymentMethod.item.pricing.total_fees;
            checkoutDetails.total_price += checkoutDetails.payment_method_fee;
        }
        // Expose and emit the computed values
        this.checkout_details.vats.percentages = checkoutDetails.vats.percentages;
        this.checkout_details.vats.total = checkoutDetails.vats.total;
        this.checkout_details.payment_method_fee = checkoutDetails.payment_method_fee;
        this.checkout_details.subtotal_price = checkoutDetails.subtotal_price;
        this.checkout_details.total_price = checkoutDetails.total_price;
        this.checkout_details.busy = (this._pendingActions > 0);
    }
    /**
     *  Computes the total fees, based on the current total price
     *  of the order, for every available payment method fee.
     */
    _computePaymentMethodFees(checkoutDetails) {
        for (const paymentMethod of this._data.payment_methods) {
            paymentMethod.pricing.total_fees = 0;
            // Fees only apply if there is something to pay for in the cart
            if (checkoutDetails.total_price === 0) {
                continue;
            }
            paymentMethod.pricing.total_fees += paymentMethod.pricing.fees.fixed;
            paymentMethod.pricing.total_fees += Math.ceil((paymentMethod.pricing.fees.percentage / 100) * checkoutDetails.total_price);
        }
    }
    /**
     *  Emit the checkout details
     *  (i) Emit the value instead of the ref to prevent race conditions
     */
    _emitCheckoutDetails() {
        this.emit('checkout', JSON.parse(JSON.stringify(this.checkout_details)));
    }
    /**
     *  Format a price in cents to the correct currency format
     */
    _formatPrice(cents) {
        if (typeof cents !== 'number') {
            cents = Number.parseFloat(cents);
        }
        if (Number.isNaN(cents)) {
            return '???';
        }
        return this._priceFormatter.format(cents / 100);
    }
    /**
     *  Register the on unload event handlers, so reservations CAN be released on window close.
     */
    _registerOnUnload() {
        window.addEventListener('unload', this._unloadHandler.bind(this));
    }
    /**
     *  Handler method factory for onunload event. The returned handler will try to release any reservations when closing the window.
     *
     *  Note: There is no fail-proof way to do this, as browser implementations
     *  do not always allow processes to finish after DOM unload.
     *  However, the this method uses the FETCH API with keepalive,
     *  which SHOULD in theory allow the request to exist without its parent
     *  or anything waiting for its response.
     */
    _unloadHandler() {
        // IE (and some other ancient browsers) do not implement the Fetch API.
        // For those browsers we will silently fail, as this is accepted behaviour.
        if (!window.fetch) {
            return;
        }
        const releaseCbFactory = (type) => {
            return (cartItem) => {
                if (cartItem.childOf) {
                    // Ignore children, as they will be released with the parent anyway, no need to waste cycles.
                    return true;
                }
                // noinspection JSIgnoredPromiseFromCall
                this._reservator.releaseOnUnload(type, cartItem.item.guid, cartItem.reservation.reservation);
            };
        };
        forOwn(this.flatItems.tickets, releaseCbFactory(ReservationType.Ticket));
        forOwn(this.flatItems.products, releaseCbFactory(ReservationType.Product));
    }
}
/**
 *  This function will create the default booker info collection.
 *
 *  A booker info collection is a collection of metadata like objects
 *  that are used for fixed/forced order data that require
 *  input from the user and should be validated.
 *  The booker info interface is designed so it can be used easily
 *  with the same methods used for regular metadata.
 */
export function factoryBookerCartMetadata(emailValidationRule) {
    // TODO Figure out real translatable name -> this property should hold already translated name ??
    // TODO Add _slug for translation slug -> so translations can be done on the fly (for both shopdata metadata & 'fake metadata')
    const emailValidationRules = ['required', 'email'];
    if (emailValidationRule) {
        emailValidationRules.push(emailValidationRule);
    }
    return {
        firstname: {
            item: {
                name: 'first_name',
                extra: ['required'],
                type: MetadataType.String,
                auto_fill_facebook: 'first_name',
                translateName: 'shop.common.metaData.first_name',
                translatable: true,
            },
            value: null,
            errors: [],
        },
        lastname: {
            item: {
                name: 'last_name',
                extra: ['required'],
                type: MetadataType.String,
                auto_fill_facebook: 'last_name',
                translateName: 'shop.common.metaData.last_name',
                translatable: true,
            },
            value: null,
            errors: [],
        },
        email: {
            item: {
                name: 'email',
                extra: emailValidationRules,
                type: MetadataType.String,
                auto_fill_facebook: 'email',
                translateName: 'shop.common.metaData.email',
                translatable: true,
            },
            value: null,
            errors: [],
        },
    };
}
/**
 *  Calculate the difference to the next valid count for a ticket,
 *  from the given current ticket count.
 *
 *  This method is needed due to the constraints on tickets.
 *  These constraints should be adhered to even when reserving/releasing ticket reservations.
 *
 *  Any diff returned will result in both constraints being valid:
 *  1) (currentTicketCount + d) % increment === 0
 *  2) (currentTicketCount + d) === 0 || (currentTicketCount + d) > min
 *
 *  Depending on the sign of direction, this can either be a positive or negative diff.
 */
export function calculateTicketDiff(currentTicketCount, min, increment, direction = 1) {
    let diffToNext;
    if (direction > 0) { // When direction is positive:
        // First calculate closest multiple of increment bigger than current
        const quotient = Math.floor(currentTicketCount / increment);
        const next = (quotient + 1) * increment;
        diffToNext = next - currentTicketCount;
        if (next < min) {
            const tooLow = min - next;
            const tooLowQuotient = Math.ceil(tooLow / increment);
            diffToNext += tooLowQuotient * increment;
        }
    }
    else { // When direction is negative
        // First calculate closest multiple of increment smaller than current
        const quotient = Math.ceil(currentTicketCount / increment);
        diffToNext = currentTicketCount - (quotient - 1) * increment;
        if ((currentTicketCount - diffToNext) < min) {
            // When next ticket count is less than minimum,
            // The next step should be zero.
            diffToNext = currentTicketCount;
        }
    }
    return diffToNext;
}
//# sourceMappingURL=cart.js.map